/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GetIntakeSubmissionApprovalStatusOutput,
  IntakeWorkflowRuleTypeSchema,
} from "@src/sudozi-schemas/get-intake-submission-approval-status.schema";
import {
  GetIntakeSubmissionSchema,
  IntakeAnswer,
} from "@src/sudozi-schemas/get-intake-submission-schema";
import { GetIntakeSubmissionTabsOutput } from "@src/sudozi-schemas/get-intake-submission-tabs.schema";
import {
  GetIntakeSubmissionThreadsOutput,
  GetLevelGroupStatusThreads,
} from "@src/sudozi-schemas/get-intake-submission-threads.schema";
import { PostIntakeSubmissionApprovalInputSchema } from "@src/sudozi-schemas/post-intake-submission-approval-schema";
import { spendRequestForFinancialAnalysisSchema } from "@src/sudozi-schemas/spend-request-for-financial-analysis.schema";
import { z, ZodEnum } from "zod";
import {
  CreateIntakeSubmissionFieldSchema,
  CreateIntakeSubmissionSchema,
  IntakeCategoryType,
  IntakeFormStepSchema,
  IntakeQuestionTypeSchema,
  IntakeSubmissionNeedsAttentionSchema,
} from "@src/sudozi-schemas/intake-submission-schemas";
import { GetIntakeSubmissionDocumentsOutput } from "@src/sudozi-schemas/get-intake-submissions-documents.schema";
import { GetIntakeSubmissionServicesFromAnswersOutput } from "@src/sudozi-schemas/get-intake-submission-services-from-answers.schema";
import {
  CreateIntakeQuestionSchema,
  IntakeQuestionSchema,
} from "@src/sudozi-schemas/intake-steps-schema";
import { DocumentTypeSchema } from "@src/sudozi-schemas/document-type-schema";
import {
  MemberCreateSchema,
  MemberObjectSchema,
} from "@src/sudozi-schemas/member-object-schema";
import { RequestWatcherTypeSchema } from "@src/sudozi-schemas/request-watcher-type";
import { IntakeSubmissionWithCustomColumnsSchema } from "@src/sudozi-schemas/intake-submission-with-custom-columns-schema";
import { RequestApproverTypeSchema } from "@src/sudozi-schemas/request-approver-type-schema";
import { IntakeViewerSchema } from "@src/sudozi-schemas/intake-viewer-schema";
import { BillApproversSchema } from "@src/sudozi-schemas/bill-approvers-schema";
import { BillSchema, CreateBillSchema } from "@src/sudozi-schemas/bill-schema";
import { BillPaymentSchema } from "@src/sudozi-schemas/bill-payment-schema";
import {
  BlockStatusSchema,
  RequestStatusSchema,
} from "@src/sudozi-schemas/request-status-schema";
import { AuditLogTypeSchema } from "@src/sudozi-schemas/audit-log-type";
import { ErpEntitySchema } from "@src/sudozi-schemas/erp-entity-schema";
import { VendorWithLast12Schema } from "@src/sudozi-schemas/get-vendors-with-last12-schemas";
import { LedgerEntrySchema } from "@src/sudozi-schemas/ledger-entry-schemas";
import { ServiceListingSchema } from "@src/sudozi-schemas/service-schemas";
import { GetWorkflowSchemasSchema } from "@src/sudozi-schemas/ironclad/get-workflow-schemas-schema";
import { IntakeSubmissionIroncladWorkflowValueSchema } from "@src/sudozi-schemas/intake-submission-ironclad-workflow-values.schema";
import { PurchaseOrderSchema } from "@src/sudozi-schemas/purchase-order-schema";
import { PaymentTermsSchema } from "@src/sudozi-schemas/payment-terms.schema";
import { RequestNotificationLevelSchema } from "@src/sudozi-schemas/request-notification-level-schema";
import { PaymentFrequencySchema } from "@src/sudozi-schemas/PaymentFrequencySchema";
import { PaymentMethodSchema } from "@src/sudozi-schemas/payment-method-schema";

type ApiMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
type ApiRoute = `${ApiMethod} /api/${string}`;

export type ApiRouteSchema = {
  params: z.ZodSchema;
  query: z.ZodSchema;
  body: z.ZodSchema;
  output: z.ZodSchema;
  stream?: boolean;
};

export type ApiRouteToSchemaMap = {
  [key in ApiRoute]: ApiRouteSchema;
};

function paginatedOutput<O extends z.ZodSchema>(outputSchema: O) {
  return z.object({
    edges: z.array(z.object({ cursor: z.string(), node: outputSchema })),
    pageInfo: z.object({
      hasNextPage: z.boolean(),
      endCursor: z.string().nullable(),
    }),
  });
}

function paginatedQuery<C extends ZodEnum<any>, Q extends z.ZodObject<any>>(
  columns: C,
  querySchema: Q,
) {
  return z
    .object({
      cursor: z.string().optional(),
      orderBy: z
        .object({
          by: columns.array(),
          dir: z.enum(["ASC", "DESC"]).array(),
        })
        .optional(),
    })
    .merge(querySchema);
}

export type QueryRoute = {
  method: "GET";
  path: `/api/${string}`;
  params: z.ZodSchema;
  query: z.ZodSchema;
  body: z.ZodSchema;
  output: z.ZodSchema;
  stream?: boolean;
};

export type MutationRoute = Omit<QueryRoute, "method"> & {
  method: "POST" | "PUT" | "PATCH" | "DELETE";
};

export type Route = Omit<MutationRoute, "method"> & {
  method: ApiMethod;
};

export type PublicRoute = Omit<QueryRoute, "method" | "path"> & {
  path: `/public-api/${string}`;
  method: ApiMethod;
};

export type PublicQueryRoute = Omit<PublicRoute, "method"> & {
  method: "GET";
};

export type PublicMutationRoute = Omit<PublicRoute, "method"> & {
  method: "POST" | "PUT" | "PATCH" | "DELETE";
};

const ChatThreadMessageSchema = z.object({
  id: z.string(),
  status: z.enum(["in_progress", "incomplete", "completed"]).optional(),
  role: z.enum(["user", "assistant"]),
  attachments: z
    .array(
      z.object({
        file_id: z.string().optional(),
        tools: z.array(z.object({ type: z.string() })).optional(),
      }),
    )
    .nullable(),
  content: z.array(
    z.union([
      z.object({
        type: z.literal("text"),
        text: z.object({
          value: z.string(),
          annotations: z.array(z.any()),
        }),
      }),
      z.object({
        type: z.literal("image_file"),
        image_file: z.object({
          file_id: z.string(),
        }),
      }),
      z.object({
        type: z.literal("image_url"),
        image_url: z.object({
          url: z.string(),
          detail: z.enum(["auto", "low", "high"]).optional(),
        }),
      }),
      z.object({
        type: z.literal("refusal"),
        refusal: z.string(),
      }),
    ]),
  ),
  created_at: z.number(),
});

export const GetChatThreadMessagesRoute = {
  method: "GET",
  path: "/api/chat-interface/threads/:threadId/messages",
  params: z.object({
    threadId: z.string(),
  }),
  query: paginatedQuery(
    z.enum(["created_at", "id"]),
    z.object({
      order: z.enum(["asc", "desc"]).optional().default("asc"),
    }),
  ),
  body: z.void(),
  output: paginatedOutput(ChatThreadMessageSchema),
} as const satisfies QueryRoute;

export const GetApiKeysRoute = {
  method: "GET",
  path: "/api/api-keys",
  params: z.void(),
  query: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      createdAt: z.string(),
      lastUsed: z.string().nullable(),
      enabled: z.boolean(),
      createdByUser: z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      }),
    }),
  ),
} as const satisfies QueryRoute;

export const PostPaymentsBillRecreateErpSyncEntryRoute = {
  method: "POST",
  path: "/api/payments/bills/:id/recreate-erp-sync-entry",
  params: z.object({
    id: z.string(),
  }),
  query: z.void(),
  body: z.void(),
  output: z.object({
    billErpSyncEntryCreated: z.boolean(),
    billPaymentErpSyncEntryCreated: z.boolean(),
  }),
} as const satisfies MutationRoute;

export const GetTransactionTotalsRoute = {
  method: "GET",
  path: "/api/transactions/totals",
  query: z.object({
    startYearMonth: z.string().nullish(),
    endYearMonth: z.string().nullish(),
    inDepartmentIds: z.array(z.string()).optional(),
    notInDepartmentIds: z.array(z.string()).optional(),
    inBusinessUnitIds: z.array(z.string()).optional(),
    notInBusinessUnitIds: z.array(z.string()).optional(),
    inCategoryIds: z.array(z.string()).optional(),
    notInCategoryIds: z.array(z.string()).optional(),
    inLocationIds: z.array(z.string()).optional(),
    notInLocationIds: z.array(z.string()).optional(),
    inVendorIds: z.array(z.string()).optional(),
    notInVendorIds: z.array(z.string()).optional(),
    type: z.enum(["expense", "revenue"]),
    accountType: z.string().nullish(),
  }),
  params: z.void(),
  body: z.void(),
  output: z.object({
    total: z.number(),
  }),
} as const satisfies QueryRoute;

export const DeleteApiKeyRoute = {
  method: "DELETE",
  path: "/api/api-keys/:apiKeyId",
  params: z.object({
    apiKeyId: z.string(),
  }),
  query: z.void(),
  body: z.void(),
  output: z.void(),
} as const satisfies MutationRoute;

export const PutApiKeyEnabledRoute = {
  method: "PUT",
  path: "/api/api-keys/:apiKeyId/enabled",
  params: z.object({
    apiKeyId: z.string(),
  }),
  query: z.void(),
  body: z.object({
    enabled: z.boolean(),
  }),
  output: z.void(),
} as const satisfies MutationRoute;

export const PostApiKeyRoute = {
  method: "POST",
  path: "/api/api-keys",
  params: z.void(),
  query: z.void(),
  body: z.object({
    name: z
      .string()
      .max(32)
      .regex(/^[a-zA-Z0-9-]+$/),
  }),
  output: z.object({
    id: z.string(),
    name: z.string(),
    key: z.string(),
  }),
} as const satisfies MutationRoute;

export const PostCompleteWebhookTaskRoute = {
  method: "POST",
  path: "/api/webhook-tasks/:levelGroupStatusId/complete",
  params: z.object({
    levelGroupStatusId: z.string(),
  }),
  query: z.void(),
  body: z.object({
    url: z.string(),
    fields: z.array(
      z.object({
        key: z.string(),
        value: z.string(),
      }),
    ),
  }),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetVendorArchivedNotificationsSettingsRoute = {
  method: "GET",
  path: "/api/vendor-notifications-settings",
  params: z.void(),
  query: z.void(),
  body: z.void(),
  output: z.object({
    sendToAdmins: z.boolean(),
    sendToVendorAdmins: z.boolean(),
    sendToVendorOwners: z.boolean(),
    groupsToSendTo: z
      .object({
        id: z.string(),
        name: z.string(),
      })
      .array(),
  }),
} as const satisfies QueryRoute;

export const PostVendorArchivedNotificationsSettingsRoute = {
  method: "POST",
  path: "/api/vendor-notifications-settings",
  params: z.void(),
  query: z.void(),
  body: z.object({
    sendToAdmins: z.boolean(),
    sendToVendorAdmins: z.boolean(),
    sendToVendorOwners: z.boolean(),
    groupsToSendTo: z.string().array(),
  }),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetThreadsRoute = {
  method: "GET",
  path: "/api/threads",
  params: z.void(),
  query: paginatedQuery(
    z.enum(["createdAt", "id"]),
    z.object({
      targetType: z.enum([
        "vendor",
        "service",
        "document",
        "bill",
        "purchaseOrder",
      ]),
      targetId: z.string(),
    }),
  ),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      id: z.string(),
      createdAt: z.string(),
      resolvedAt: z.string().nullable(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetThreadCommentLinesRoute = {
  method: "GET",
  path: "/api/threads/:threadId/comment-lines",
  params: z.object({
    threadId: z.string(),
  }),
  query: paginatedQuery(z.enum(["createdAt", "id"]), z.object({})),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      id: z.string(),
      createdAt: z.string(),
      updatedAt: z.string(),
      deletedAt: z.string().nullable(),
      body: z.string(),
      createdByUser: z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      }),
      documents: z.array(z.object({ id: z.string(), displayName: z.string() })),
      company: z.object({ id: z.string(), name: z.string() }).nullable(),
      vendorCompany: z.object({ id: z.string(), name: z.string() }).nullable(),
    }),
  ),
} as const satisfies QueryRoute;

export const PostUpdateTransactionRoute = {
  method: "POST",
  path: "/api/update-transaction",
  params: z.void(),
  query: z.void(),
  body: z.object({
    transactionId: z.string(),
    vendorId: z.string().nullable(),
    spendCategoryId: z.string().nullable(),
  }),
  output: z.void(),
} as const satisfies MutationRoute;

export const PostSearchRoute = {
  method: "POST",
  path: "/api/search",
  params: z.void(),
  query: z.void(),
  body: z.object({
    query: z.string(),
  }),
  output: z.array(
    z.object({
      vendorId: z.string().nullable(),
      intakeSubmissionId: z.string().nullable(),
      serviceId: z.string().nullable(),
      name: z.string(),
    }),
  ),
} as const satisfies MutationRoute;

export const GetCurrentPermissionsRoute = {
  method: "GET",
  path: "/api/current-permissions",
  params: z.void(),
  query: z.void(),
  body: z.void(),
  output: z.object({
    hasAdminAccess: z.boolean(),
    hasSuperViewerAccess: z.boolean(),
    hasDepartmentAccess: z.boolean(),
    hasDepartmentPayrollAccess: z.boolean(),
    hasBusinessUnitAccess: z.boolean(),
    hasBusinessUnitPayrollAccess: z.boolean(),
    hasPaymentsAccess: z.boolean(),
    hasSpendRequestAccess: z.boolean(),
    hasFinancialAnalysisAccess: z.boolean(),
    hasApprovalBypassAccess: z.boolean(),
    hasVendorAdminAccess: z.boolean(),
    hasUserAdminAccess: z.boolean(),
    isRequestCategoryOwner: z.boolean(),
    approvesRequestsForDepartment: z.boolean(),
    approvesRequestsForBusinessUnit: z.boolean(),
    submitsSpendRequestsForOthers: z.boolean(),
    departmentIds: z.string().array(),
    businessUnitIds: z.string().array(),
    groupIds: z.string().array(),
    locationId: z.string().nullable(),
  }),
} as const satisfies QueryRoute;

export const GetCurrentUserCompanyMembershipsRoute = {
  method: "GET",
  path: "/api/current-user/company-memberships",
  params: z.void(),
  query: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      hasAdminAccess: z.boolean(),
      hasDepartmentAccess: z.boolean(),
      companyId: z.string(),
      company: z.object({
        id: z.string(),
        name: z.string(),
      }),
    }),
  ),
} as const satisfies QueryRoute;

export const GetCurrentUserDelegateApproversRoute = {
  method: "GET",
  path: "/api/current-user/delegate-approvers",
  params: z.void(),
  query: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      note: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const PostCurrentUserDelegateApproversRoute = {
  method: "POST",
  path: "/api/current-user/delegate-approvers",
  params: z.void(),
  query: z.void(),
  body: z.object({
    userId: z.string(),
    note: z.string(),
  }),
  output: z.void(),
} as const satisfies MutationRoute;

export const DeleteCurrentUserDelegateApproversRoute = {
  method: "DELETE",
  path: "/api/current-user/delegate-approvers/:userId",
  params: z.object({
    userId: z.string(),
  }),
  query: z.void(),
  body: z.void(),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetCurrentUserNotificationSettingRoute = {
  method: "GET",
  path: "/api/current-user/notification-settings",
  params: z.void(),
  query: z.void(),
  stream: false,
  body: z.void(),
  output: z.object({
    receiveSlack: z.boolean(),
    receiveEmail: z.boolean(),
    spendApproverNotificationLevel: RequestNotificationLevelSchema,
    spendWatcherNotificationLevel: RequestNotificationLevelSchema,
    requesterNotificationLevel: RequestNotificationLevelSchema,
  }),
} as const satisfies QueryRoute;

export const PostCurrentUserNotificationSettingRoute = {
  method: "POST",
  path: "/api/current-user/notification-settings",
  params: z.void(),
  query: z.void(),
  body: z.object({
    receiveSlack: z.boolean(),
    receiveEmail: z.boolean(),
    spendApproverNotificationLevel: RequestNotificationLevelSchema,
    spendWatcherNotificationLevel: RequestNotificationLevelSchema,
    requesterNotificationLevel: RequestNotificationLevelSchema,
  }),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetAuditLogsRoute = {
  method: "GET",
  path: "/api/audit-logs",
  params: z.void(),
  query: paginatedQuery(
    z.enum(["createdAt", "id"]),
    z.object({
      targetId: z.string().optional(),
      targetType: AuditLogTypeSchema,
    }),
  ),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      id: z.string(),
      createdAt: z.string(),
      message: z.string(),
      changeData: z.string().nullable(),
      user: z
        .object({
          id: z.string(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string(),
          hasAdminAccess: z.boolean(),
          hasSuperViewerAccess: z.boolean(),
          hasDepartmentAccess: z.boolean(),
          hasDepartmentPayrollAccess: z.boolean(),
          hasBusinessUnitAccess: z.boolean(),
          hasBusinessUnitPayrollAccess: z.boolean(),
          hasPaymentsAccess: z.boolean(),
          hasFinancialAnalysisAccess: z.boolean(),
          hasApprovalBypassAccess: z.boolean(),
          hasVendorAdminAccess: z.boolean(),
          hasSpendRequestAccess: z.boolean(),
          departments: z.array(
            z.object({
              id: z.string(),
              name: z.string(),
            }),
          ),
          businessUnits: z.array(
            z.object({
              id: z.string(),
              name: z.string(),
            }),
          ),
          location: z
            .object({
              id: z.string(),
              name: z.string(),
            })
            .nullable(),
        })
        .nullish(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetDocumentRoute = {
  method: "GET",
  path: "/api/documents/:documentId",
  params: z.object({ documentId: z.string() }),
  query: z.void(),
  body: z.void(),
  output: z.object({
    id: z.string(),
    name: z.string(),
    type: z.array(DocumentTypeSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
    mimeType: z.string(),
    displayName: z.string(),
    description: z.string().nullable(),
    signedDownloadUrl: z.string(),
    uploadedByEmail: z.string().nullable(),
    uploadedByUser: z
      .object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      })
      .nullable(),
    vendor: z
      .object({
        id: z.string(),
        name: z.string(),
      })
      .nullable(),
    relatedServices: z.array(ServiceListingSchema),
    ironcladDocument: z
      .object({
        ironcladDocumentId: z.string(),
        ironcladWorkflowReadableId: z.string(),
      })
      .nullable(),
    summary: z.string().nullable(),
  }),
} as const satisfies QueryRoute;

export const PostUserRoute = {
  method: "POST",
  path: "/api/users",
  query: z.void(),
  params: z.void(),
  body: z.object({
    email: z.string(),
  }),
  output: z.object({
    id: z.string(),
    email: z.string(),
  }),
} as const satisfies MutationRoute;

export const GetIroncladConfigurationRoute = {
  method: "GET",
  path: "/api/ironclad/configuration",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.object({
    ironcladCompanyId: z.string().nullable(),
  }),
} as const satisfies QueryRoute;

export const DeleteIroncladConfigurationRoute = {
  method: "DELETE",
  path: "/api/ironclad/configuration",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetAtlassianConfigurationRoute = {
  method: "GET",
  path: "/api/atlassian/configuration",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.object({
    connected: z.boolean(),
    jiraBaseUrl: z.string().nullable(),
  }),
} as const satisfies QueryRoute;

export const GetAtlassianResourcesRoute = {
  method: "GET",
  path: "/api/atlassian-resources",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      url: z.string(),
      name: z.string(),
      scopes: z.array(z.string()),
      avatarUrl: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetJiraStatusesRoute = {
  method: "GET",
  path: "/api/atlassian/projects/:projectId/statuses",
  query: z.void(),
  params: z.object({
    projectId: z.string(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string(),
      iconUrl: z.string(),
      statusCategory: z.object({
        id: z.number(),
        key: z.string(),
        colorName: z.string(),
        name: z.string(),
      }),
    }),
  ),
} as const satisfies QueryRoute;

export const GetJiraContextValuesRoute = {
  method: "GET",
  path: "/api/atlassian/resources/:resourceId/context-values",
  query: z.void(),
  params: z.object({
    resourceId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    projects: z.array(
      z.object({
        expand: z.string(),
        self: z.string(),
        id: z.string(),
        key: z.string(),
        name: z.string(),
        projectTypeKey: z.string(),
        simplified: z.boolean(),
        style: z.string(),
        isPrivate: z.boolean(),
        issueTypes: z.array(
          z.object({
            self: z.string(),
            id: z.string(),
            description: z.string(),
            iconUrl: z.string(),
            name: z.string(),
            subtask: z.boolean(),
            hierarchyLevel: z.number(),
          }),
        ),
      }),
    ),
    statuses: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    ),
  }),
} as const satisfies QueryRoute;

export const GetIroncladWorkflowsRoute = {
  method: "GET",
  path: "/api/ironclad/workflows",
  params: z.void(),
  query: z.void(),
  body: z.void(),
  output: GetWorkflowSchemasSchema,
} as const satisfies QueryRoute;

export const PostOktaConfigurationRoute = {
  method: "POST",
  path: "/api/okta-configuration",
  params: z.void(),
  query: z.void(),
  body: z.object({
    domain: z.string(),
    clientId: z.string(),
    clientSecret: z.string(),
    loginDomains: z.string().array(),
  }),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetOktaConfigurationRoute = {
  method: "GET",
  path: "/api/okta-configuration",
  params: z.void(),
  query: z.void(),
  body: z.void(),
  output: z.object({
    domain: z.string(),
    clientId: z.string(),
    clientSecret: z.string(),
    loginDomains: z.string().array(),
  }),
} as const satisfies QueryRoute;

export const GetSamlEntityMappingsRoute = {
  method: "GET",
  path: "/api/saml-entity-mappings",
  params: z.void(),
  query: paginatedQuery(z.enum(["integrationEntity", "id"]), z.object({})),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      entityId: z.string().nullable(),
      integrationEntity: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const PostSamlEntityMappingsRoute = {
  method: "POST",
  path: "/api/saml-entity-mappings",
  params: z.void(),
  query: z.void(),
  body: z.array(
    z.object({
      entityId: z.string().nullable(),
      integrationEntity: z.string(),
    }),
  ),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetSamlBusinessUnitMappingsRoute = {
  method: "GET",
  path: "/api/saml-business-unit-mappings",
  params: z.void(),
  query: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      businessUnitId: z.string().nullable(),
      integrationBusinessUnit: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const PostSamlBusinessUnitMappingsRoute = {
  method: "POST",
  path: "/api/saml-business-unit-mappings",
  params: z.void(),
  query: z.void(),
  body: z.array(
    z.object({
      businessUnitId: z.string().nullable(),
      integrationBusinessUnit: z.string(),
    }),
  ),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetSamlDepartmentMappingsRoute = {
  method: "GET",
  path: "/api/saml-department-mappings",
  params: z.void(),
  query: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      departmentId: z.string().nullable(),
      integrationDepartment: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const PostSamlDepartmentMappingsRoute = {
  method: "POST",
  path: "/api/saml-department-mappings",
  params: z.void(),
  query: z.void(),
  body: z.array(
    z.object({
      departmentId: z.string().nullable(),
      integrationDepartment: z.string(),
    }),
  ),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetSamlMappingsNeedsAttentionRoute = {
  method: "GET",
  path: "/api/saml-mappings-needs-attention",
  params: z.void(),
  query: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      mappingType: z.enum(["department", "businessUnit"]),
      count: z.number(),
      createdAt: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetOktaSamlConfigurationRoute = {
  method: "GET",
  path: "/api/okta-saml-configuration",
  params: z.void(),
  query: z.void(),
  body: z.void(),
  output: z.object({
    samlMetadataUrl: z.string(),
    loginDomains: z.string().array(),
    scimApiKey: z.string(),
    scimEndpoint: z.string(),
  }),
} as const satisfies QueryRoute;

export const PostOktaSamlConfigurationRoute = {
  method: "POST",
  path: "/api/okta-saml-configuration",
  params: z.void(),
  query: z.void(),
  body: z.object({
    samlMetadataUrl: z.string(),
    loginDomains: z.string().array(),
  }),
  output: z.void(),
} as const satisfies MutationRoute;

export const DeleteMemberDelegateApproversRoute = {
  method: "DELETE",
  path: "/api/members/:memberId/delegate-approvers/:delegateUserId",
  params: z.object({
    memberId: z.string(),
    delegateUserId: z.string(),
  }),
  query: z.void(),
  body: z.void(),
  output: z.void(),
} as const satisfies MutationRoute;

export const PostMemberDelegateApproversRoute = {
  method: "POST",
  path: "/api/members/:memberId/delegate-approvers",
  params: z.object({ memberId: z.string() }),
  query: z.void(),
  body: z.object({ delegateUserId: z.string(), note: z.string() }),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetMemberDelegateApproversRoute = {
  method: "GET",
  path: "/api/members/:memberId/delegate-approvers",
  params: z.object({ memberId: z.string() }),
  query: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      delegateEntryId: z.string(),
      delegateUserId: z.string(),
      note: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const PostMemberEnabledRoute = {
  method: "POST",
  path: "/api/members/:memberId/enabled",
  params: z.object({ memberId: z.string() }),
  query: z.void(),
  body: z.object({ enabled: z.boolean() }),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetMemberRoute = {
  method: "GET",
  path: "/api/members/:memberId",
  params: z.object({ memberId: z.string() }),
  query: z.void(),
  body: z.void(),
  output: MemberObjectSchema,
} as const satisfies QueryRoute;

export const GetMembersStatsRoute = {
  method: "GET",
  path: "/api/member-stats",
  params: z.void(),
  query: z.void(),
  body: z.void(),
  output: z.object({
    activeMembers: z.number(),
    admins: z.number(),
    superViewers: z.number(),
    leads: z.number(),
    members: z.number(),
  }),
} as const satisfies QueryRoute;

export const GetMembersRoute = {
  method: "GET",
  path: "/api/members",
  params: z.void(),
  query: paginatedQuery(
    z.enum(["firstName", "lastName", "id", "email"]),
    z.object({
      inGroupId: z.string().optional(),
      enabled: z.enum(["true", "false"]).optional(),
      notInGroupId: z.string().optional(),
      inDepartmentId: z.string().optional(),
      notInDepartmentId: z.string().optional(),
      inBusinessUnitId: z.string().optional(),
      notInBusinessUnitId: z.string().optional(),
      userIds: z.string().array().optional(),
      name: z.string().optional(),
      email: z.string().optional(),
    }),
  ),
  body: z.void(),
  output: paginatedOutput(MemberObjectSchema),
} as const satisfies QueryRoute;

export const GetMembersCsvRoute = {
  method: "GET",
  path: "/api/members.csv",
  params: z.void(),
  query: paginatedQuery(
    z.enum(["firstName", "lastName", "id", "email"]),
    z.object({
      inGroupId: z.string().optional(),
      enabled: z.enum(["true", "false"]).optional(),
      notInGroupId: z.string().optional(),
      inDepartmentId: z.string().optional(),
      notInDepartmentId: z.string().optional(),
      inBusinessUnitId: z.string().optional(),
      notInBusinessUnitId: z.string().optional(),
      userIds: z.string().array().optional(),
      name: z.string().optional(),
      email: z.string().optional(),
    }),
  ),
  body: z.void(),
  output: z.void(),
  stream: true,
} as const satisfies QueryRoute;

export const PostMemberNewRoute = {
  method: "POST",
  path: "/api/member/new",
  params: z.void(),
  query: z.void(),
  body: MemberCreateSchema,
  output: z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    timezone: z.string(),
  }),
} as const satisfies MutationRoute;

export const PostMemberUpdateRoute = {
  method: "POST",
  path: "/api/member/:memberId/update",
  params: z.object({
    memberId: z.string(),
  }),
  query: z.void(),
  body: MemberCreateSchema,
  output: z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    timezone: z.string(),
  }),
} as const satisfies MutationRoute;

export const PutMemberNotificationSettingsRoute = {
  method: "PUT",
  path: "/api/members/:memberId/notification-settings",
  params: z.object({ memberId: z.string() }),
  query: z.void(),
  body: z.object({
    receiveEmail: z.boolean(),
    receiveSlack: z.boolean(),
  }),
  output: z.object({
    receiveEmail: z.boolean(),
    receiveSlack: z.boolean(),
  }),
} as const satisfies MutationRoute;

export const PostMembersSendNewMemberInviteRoute = {
  method: "POST",
  path: "/api/members/send-new-member-invite",
  params: z.void(),
  query: z.void(),
  body: z.object({
    userIds: z.string().array(),
  }),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetDepartmentsRoute = {
  method: "GET",
  path: "/api/departments",
  params: z.void(),
  query: paginatedQuery(
    z.enum(["name", "createdAt"]),
    z.object({
      departmentIds: z.string().array().nullish(),
    }),
  ),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetBusinessUnitsRoute = {
  method: "GET",
  path: "/api/business-units",
  params: z.void(),
  query: paginatedQuery(
    z.enum(["name", "createdAt"]),
    z.object({
      businessUnitIds: z.string().array().nullish(),
    }),
  ),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetSpendCategoriesRoute = {
  method: "GET",
  path: "/api/spend-categories",
  params: z.void(),
  query: paginatedQuery(
    z.enum(["name", "createdAt"]),
    z.object({
      spendCategoryIds: z.string().array().nullish(),
    }),
  ),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetVendorSelectOptionsRoute = {
  method: "GET",
  path: "/api/vendor-select-options",
  params: z.void(),
  query: paginatedQuery(
    z.enum(["name", "createdAt"]),
    z.object({
      search: z.string().nullish(),
      ids: z.string().array().nullish(),
    }),
  ),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      label: z.string(),
      value: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetPlanVendorSelectOptionsRoute = {
  method: "GET",
  path: "/api/plan-vendor-select-options",
  params: z.void(),
  query: paginatedQuery(
    z.enum(["name", "createdAt"]),
    z.object({
      search: z.string().nullish(),
      planId: z.string(),
    }),
  ),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      label: z.string(),
      value: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetSelectServicesRoute = {
  method: "GET",
  path: "/api/select-services",
  params: z.void(),
  query: z.object({
    search: z.string().nullish(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetMemberSelectOptionsRoute = {
  method: "GET",
  path: "/api/member-select-options",
  params: z.void(),
  query: z.object({
    search: z.string().nullish(),
    isSigner: z.enum(["true", "false"]).optional(),
    inGroupIds: z.string().array().nullish(),
    approverForDepartmentIds: z.string().array().nullish(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetGroupSelectOptionsRoute = {
  method: "GET",
  path: "/api/group-select-options",
  params: z.void(),
  query: z.object({
    search: z.string().nullish(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetDepartmentSelectOptionsRoute = {
  method: "GET",
  path: "/api/department-select-options",
  params: z.void(),
  query: z.object({
    search: z.string().nullish(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetLocationSelectOptionsRoute = {
  method: "GET",
  path: "/api/location-select-options",
  params: z.void(),
  query: z.object({
    search: z.string().nullish(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetSpendCategorySelectOptionsRoute = {
  method: "GET",
  path: "/api/spend-category-select-options",
  params: z.void(),
  query: paginatedQuery(
    z.enum(["name", "createdAt"]),
    z.object({
      search: z.string().nullish(),
    }),
  ),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      label: z.string(),
      value: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetIntakeQuestionsRoute = {
  method: "GET",
  path: "/api/intake-questions-by-category/:intakeCategoryId",
  params: z.object({ intakeCategoryId: z.string() }),
  query: z.object({
    types: z.array(IntakeQuestionTypeSchema).optional(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      description: z.string(),
      isRequired: z.boolean(),
      type: IntakeQuestionTypeSchema,
      isMulti: z.boolean(),
      ordinal: z.number(),
      chainOperator: z.enum(["AND", "OR"]),
      showOnVendorDetails: z.boolean(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetBusinessUnitSelectOptionsRoute = {
  method: "GET",
  path: "/api/business-unit-select-options",
  params: z.void(),
  query: z.object({
    search: z.string().nullish(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetPublicIntakeCategoryRoute = {
  method: "GET",
  path: "/public-api/intake-categories/:intakeCategoryId",
  params: z.object({ intakeCategoryId: z.string() }),
  query: z.void(),
  body: z.void(),
  output: z.object({
    id: z.string(),
    companyId: z.string(),
    name: z.string(),
    description: z.string(),
    ordinal: z.number(),
    isReadOnly: z.boolean(),
    type: IntakeCategoryType,
    createdAt: z.string(),
    updatedAt: z.string(),
    workflows: z.array(
      z.object({
        id: z.string(),
        label: z.string(),
      }),
    ),
  }),
} as const satisfies PublicRoute;

export const GetErpVendorSelectOptionsRoute = {
  method: "GET",
  path: "/api/erp-vendor-select-options",
  params: z.void(),
  query: paginatedQuery(
    z.enum(["name", "createdAt"]),
    z.object({
      search: z.string().nullish(),
      erpEntityId: z.string().nullish(),
    }),
  ),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      label: z.string(),
      value: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetIntakeWorkflowRoute = {
  method: "GET",
  path: "/api/intake-workflow/:intakeWorkflowId",
  query: z.void(),
  params: z.object({
    intakeWorkflowId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    id: z.string(),
    label: z.string(),
    anyOfEntryPoints: z
      .object({
        id: z.string(),
        values: z.string().array(),
        intakeQuestionId: z.string(),
        intakeQuestion: z.object({
          id: z.string(),
          label: z.string(),
          type: IntakeQuestionTypeSchema,
        }),
        value: z.any(),
      })
      .array(),
    numericEntryPoints: z
      .object({
        id: z.string(),
        minimumValue: z.number().nullable(),
        maximumValue: z.number().nullable(),
        intakeQuestionId: z.string(),
        intakeQuestion: z.object({
          id: z.string(),
          label: z.string(),
          type: IntakeQuestionTypeSchema,
        }),
        value: z.any(),
      })
      .array(),
    booleanEntryPoints: z
      .object({
        id: z.string(),
        value: z.boolean(),
        intakeQuestionId: z.string(),
        intakeQuestion: z.object({
          id: z.string(),
          label: z.string(),
          type: IntakeQuestionTypeSchema,
        }),
      })
      .array(),
    ruleLevels: z.array(
      z.object({
        id: z.string(),
        label: z.string(),
        levelNumber: z.number(),
        intakeStepId: z.string().nullable(),
        rules: z.array(
          z.object({
            id: z.string(),
            approverType: RequestApproverTypeSchema,
            watcherType: RequestWatcherTypeSchema,
            label: z.string(),
            type: IntakeWorkflowRuleTypeSchema,
            description: z.string().nullable(),
            chainOperator: z.enum(["AND", "OR"]),
            anyOfConditions: z.array(
              z.object({
                id: z.string(),
                values: z.string().array(),
                questionId: z.string(),
                question: z.object({
                  id: z.string(),
                  label: z.string(),
                  type: IntakeQuestionTypeSchema,
                }),
              }),
            ),
            numericConditions: z.array(
              z.object({
                id: z.string(),
                minimumValue: z.number().nullable(),
                maximumValue: z.number().nullable(),
                questionId: z.string(),
                question: z.object({
                  id: z.string(),
                  label: z.string(),
                  type: IntakeQuestionTypeSchema,
                }),
              }),
            ),
            booleanConditions: z.array(
              z.object({
                id: z.string(),
                value: z.boolean(),
                questionId: z.string(),
                question: z.object({
                  id: z.string(),
                  label: z.string(),
                  type: IntakeQuestionTypeSchema,
                }),
              }),
            ),
            approverGroups: z.array(
              z.object({
                id: z.string(),
                name: z.string(),
              }),
            ),
            watcherGroups: z.array(
              z.object({
                id: z.string(),
                name: z.string(),
              }),
            ),
            sendVendorFormTask: z
              .object({
                vendorIntakeCategories: z
                  .object({
                    id: z.string(),
                    name: z.string(),
                  })
                  .array(),
                vendorQuestionId: z.string().nullable(),
                emailQuestionId: z.string().nullable(),
                emailCopy: z.string().nullable(),
                autoSend: z.boolean(),
              })
              .nullable(),
            sendDocumentTask: z
              .object({
                autoSend: z.boolean(),
                emailQuestionId: z.string().nullable(),
                emailCopy: z.string().nullable(),
                emailRecipientSource: z.enum(["REQUESTER", "QUESTION"]),
                documents: z
                  .object({ displayName: z.string(), id: z.string() })
                  .array(),
              })
              .nullable(),
            startIroncladWorkflow: z
              .object({
                ironcladWorkflowId: z.string(),
              })
              .nullable(),
            additionalStepTask: z
              .object({
                steps: z
                  .object({
                    id: z.string(),
                    label: z.string().nullable(),
                    ordinal: z.number(),
                  })
                  .array(),
                workflowType: z.enum(["SUB_LEVELS", "APPEND_LEVELS_TO_END"]),
              })
              .nullable(),
            createJiraIssueTask: z
              .object({
                syncIssueStatus: z.boolean(),
                resourceId: z.string(),
                projectId: z.string(),
                issueTypeId: z.string(),
                descriptionQuestionIds: z.string().array(),
                rejectionStatusId: z.string().nullable(),
              })
              .nullable(),
            middeskBusinessVerificationTask: z
              .object({
                vendorQuestionId: z.string().nullable(),
                businessNameQuestionId: z.string(),
                businessAddressQuestionId: z.string(),
                businessTaxIdQuestionId: z.string().nullable(),
              })
              .nullable(),
            intakeWorkflowWebhook: z
              .object({
                url: z.string(),
                mappings: z.array(
                  z.object({
                    key: z.string(),
                    intakeQuestionId: z.string(),
                  }),
                ),
              })
              .nullable(),
            intakeWorkflowRuleApproverMatrix: z
              .object({
                id: z.string(),
                approverMatrixId: z.string(),
                approverMatrixApproverColumnId: z.string(),
                approverMatrixConditionColumnMappings: z.array(
                  z.object({
                    id: z.string(),
                    approverMatrixConditionColumnId: z.string(),
                    type: z.enum(["QUESTION", "REQUESTOR_DEPARTMENT"]),
                    intakeQuestionId: z.string().nullable(),
                  }),
                ),
              })
              .nullable(),
          }),
        ),
      }),
    ),
  }),
} as const satisfies QueryRoute;

export const PostIntakeWorkflowLevelsRoute = {
  method: "POST",
  path: "/api/intake-workflow/:intakeWorkflowId/levels",
  query: z.void(),
  params: z.object({
    intakeWorkflowId: z.string(),
  }),
  output: z.boolean(),
  body: z.object({
    levels: z.array(
      z.object({
        id: z.string(),
        label: z.string(),
        levelNumber: z.number(),
        intakeStepId: z.string().nullish(),
        rules: z.array(
          z.object({
            id: z.string(),
            label: z.string(),
            type: IntakeWorkflowRuleTypeSchema,
            description: z.string().nullable(),
            approverType: RequestApproverTypeSchema,
            watcherType: RequestWatcherTypeSchema,
            chainOperator: z.enum(["AND", "OR"]),
            anyOfConditions: z.array(
              z.object({
                id: z.string(),
                intakeQuestionId: z.string(),
                values: z.string().array(),
              }),
            ),
            numericConditions: z.array(
              z.object({
                id: z.string(),
                intakeQuestionId: z.string(),
                minimumValue: z.number().nullable(),
                maximumValue: z.number().nullable(),
              }),
            ),
            booleanConditions: z.array(
              z.object({
                id: z.string(),
                intakeQuestionId: z.string(),
                value: z.boolean(),
              }),
            ),
            approverGroupIds: z.string().array(),
            watcherGroupIds: z.string().array(),
            sendVendorFormTask: z
              .object({
                vendorIntakeCategoryIds: z.string().array(),
                vendorQuestionId: z.string().nullish(),
                emailQuestionId: z.string().nullish(),
                emailCopy: z.string().nullish(),
                autoSend: z.boolean(),
              })
              .nullish(),
            sendDocumentTask: z
              .object({
                autoSend: z.boolean().nullish(),
                emailQuestionId: z.string().nullish(),
                emailCopy: z.string().nullish(),
                documentIds: z.string().array(),
              })
              .nullish(),
            startIroncladWorkflow: z
              .object({
                ironcladWorkflowId: z.string(),
              })
              .nullish(),
            additionalStepTask: z
              .object({
                stepIds: z.string().array(),
                workflowType: z
                  .enum(["SUB_LEVELS", "APPEND_LEVELS_TO_END"])
                  .nullish(),
              })
              .nullish(),
            createJiraIssueTask: z
              .object({
                syncIssueStatus: z.boolean(),
                resourceId: z.string(),
                projectId: z.string(),
                issueTypeId: z.string(),
                descriptionQuestionIds: z.string().array(),
                rejectionStatusId: z.string().nullable(),
              })
              .nullish(),
            middeskBusinessVerificationTask: z
              .object({
                vendorQuestionId: z.string().nullable(),
                businessNameQuestionId: z.string(),
                businessAddressQuestionId: z.string(),
                businessTaxIdQuestionId: z.string().nullable(),
              })
              .nullish(),
            webhook: z
              .object({
                url: z.string(),
                mappings: z.array(
                  z.object({
                    key: z.string(),
                    intakeQuestionId: z.string(),
                  }),
                ),
              })
              .nullish(),
            intakeWorkflowRuleApproverMatrix: z
              .object({
                approverMatrixId: z.string(),
                approverMatrixApproverColumnId: z.string(),
                approverMatrixConditionColumnMappings: z.array(
                  z.object({
                    approverMatrixConditionColumnId: z.string(),
                    intakeQuestionId: z.string().nullable(),
                    type: z.enum(["QUESTION", "REQUESTOR_DEPARTMENT"]),
                  }),
                ),
              })
              .nullish(),
          }),
        ),
      }),
    ),
    anyOfEntryPoints: z.array(
      z.object({
        id: z.string(),
        intakeQuestionId: z.string(),
        values: z.string().array(),
      }),
    ),
    numericEntryPoints: z.array(
      z.object({
        id: z.string(),
        intakeQuestionId: z.string(),
        minimumValue: z.number().nullable(),
        maximumValue: z.number().nullable(),
      }),
    ),
    booleanEntryPoints: z.array(
      z.object({
        id: z.string(),
        intakeQuestionId: z.string(),
        value: z.boolean(),
      }),
    ),
  }),
} as const satisfies Route;

export const GetIntakeCategoryIroncladWorkflowsRoute = {
  method: "GET",
  path: "/api/intake-categories/:intakeCategoryId/ironclad-workflows",
  query: z.void(),
  params: z.object({
    intakeCategoryId: z.string(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      ironcladWorkflowId: z.string(),
      ironcladWorkflowName: z.string(),
      attributes: z.array(
        z.object({
          id: z.string(),
          type: z.string(),
          displayName: z.string(),
          mappedQuestionId: z.string().nullable(),
          mappingType: z.enum(["SUDOZI_REQUEST_URL", "QUESTION"]),
        }),
      ),
    }),
  ),
} as const satisfies QueryRoute;

export const PostIntakeCategoryRoute = {
  method: "POST",
  path: "/api/intake-categories",
  query: z.void(),
  params: z.void(),
  body: z.object({
    name: z.string(),
    description: z.string(),
    ordinal: z.number(),
    type: IntakeCategoryType,
  }),
  output: z.object({ id: z.string() }),
} as const satisfies MutationRoute;

export const PutIntakeCategoryRoute = {
  method: "PUT",
  path: "/api/intake-categories/:intakeCategoryId",
  query: z.void(),
  params: z.object({
    intakeCategoryId: z.string(),
  }),
  body: z.object({
    name: z.string(),
    description: z.string(),
    ordinal: z.number(),
    type: IntakeCategoryType,
    isReadOnly: z.boolean(),
  }),
  output: z.void(),
} as const satisfies MutationRoute;

export const PutIntakeCategoriesRoute = {
  method: "PUT",
  path: "/api/intake-categories",
  query: z.void(),
  params: z.void(),
  body: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string(),
      ordinal: z.number(),
      type: IntakeCategoryType,
      isReadOnly: z.boolean(),
    }),
  ),
  output: z.void(),
} as const satisfies MutationRoute;

export const PostIntakeCategoryIroncladWorkflowsRoute = {
  method: "POST",
  path: "/api/intake-categories/:intakeCategoryId/ironclad-workflows",
  query: z.void(),
  params: z.object({
    intakeCategoryId: z.string(),
  }),
  body: z.array(
    z.object({
      ironcladWorkflowId: z.string(),
      ironcladAttributeKey: z.string(),
      ironcladAttributeType: z.string(),
      intakeQuestionId: z.string().nullish(),
      mappingType: z.enum(["SUDOZI_REQUEST_URL", "QUESTION"]),
    }),
  ),
  output: z.void(),
} as const satisfies MutationRoute;

export const PostReplaaceLgsApproversRoute = {
  method: "POST",
  path: "/api/replace-lgs-approvers",
  query: z.void(),
  params: z.void(),
  body: z.object({
    levelGroupStatusId: z.string(),
    approverType: RequestApproverTypeSchema,
    groupId: z.string().nullable(),
  }),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetDownloadIntakeSubmissionsRoute = {
  method: "GET",
  path: "/api/download-intake-submissions",
  params: z.void(),
  body: z.void(),
  query: paginatedQuery(
    z.enum([
      "createdAt",
      "id",
      "status",
      "requestorName",
      "number",
      "coalescedRequestAmount",
      "dueDate",
    ]),
    z.object({
      isDraft: z.union([z.literal("true"), z.literal("false")]).optional(),
      isArchived: z.union([z.literal("true"), z.literal("false")]).optional(),
      intakeCategoryIds: z.string().array().optional(),
      requesterIds: z.string().array().optional(),
      departmentIds: z.string().array().optional(),
      businessUnitIds: z.string().array().optional(),
      answerDepartmentId: z.string().optional(),
      answerBusinessUnitId: z.string().optional(),
      startYearMonthDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      endYearMonthDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      status: RequestStatusSchema.nullish(),
      isAssignedToUser: z
        .union([z.literal("true"), z.literal("false")])
        .nullish(),
      needsAttention: z
        .union([z.literal("true"), z.literal("false")])
        .nullish(),
      vendorIds: z.string().array().nullish(),
      search: z.string().optional(),
      answerFilters: z.array(z.string()).nullish(),
      currentApproverGroupIds: z.string().array().nullish(),
      assigneeId: z.string().nullish(),
    }),
  ),
  output: z.void(),
  stream: true,
} as const satisfies QueryRoute;

export const DeleteIntakeCategoryWorkflowRoute = {
  method: "DELETE",
  path: "/api/intake-category/:intakeWorkflowId/workflow",
  query: z.void(),
  params: z.object({
    intakeWorkflowId: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies MutationRoute;

export const DeleteIntakeCategoryRoute = {
  method: "DELETE",
  path: "/api/intake-category/:intakeCategoryId",
  query: z.void(),
  params: z.object({
    intakeCategoryId: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies MutationRoute;

export const PostCreateChatThreadRoute = {
  method: "POST",
  path: "/api/chat-threads",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.object({
    threadId: z.string(),
  }),
} as const satisfies MutationRoute;

export const GetIntakeSubmissionsRoute = {
  method: "GET",
  path: "/api/intake-submissions",
  query: paginatedQuery(
    z.enum([
      "createdAt",
      "id",
      "status",
      "requestorName",
      "number",
      "coalescedRequestAmount",
      "dueDate",
    ]),
    z.object({
      isDraft: z.enum(["true", "false"]).nullish(),
      isArchived: z.enum(["true", "false"]).nullish(),
      intakeCategoryIds: z.string().array().nullish(),
      status: RequestStatusSchema.or(z.literal("_NOT_PENDING")).nullish(),
      statuses: z
        .array(z.enum(["PENDING", "APPROVED", "REJECTED", "ARCHIVED"]))
        .nullish(),
      isAssignedToUser: z.enum(["true", "false"]).nullish(),
      needsAttention: z.enum(["true", "false"]).nullish(),
      startYearMonthDay: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullish(),
      endYearMonthDay: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullish(),
      departmentIds: z.string().array().nullish(),
      businessUnitIds: z.string().array().nullish(),
      approverGroupIds: z.string().array().nullish(),
      currentApproverGroupIds: z.string().array().nullish(),
      approverDepartmentIds: z.string().array().nullish(),
      answerDepartmentId: z.string().nullish(),
      answerBusinessUnitId: z.string().nullish(),
      requesterIds: z.string().array().nullish(),
      vendorIds: z.string().array().nullish(),
      serviceId: z.string().nullish(),
      documentId: z.string().nullish(),
      type: IntakeCategoryType,
      search: z.string().optional(),
      // answerFilters will be a list of question labels and values in sets of 2
      // e.g. ["Question 1", "Answer 1", "Question 2", "Answer 2"]
      answerFilters: z
        .array(z.string())
        .nullish()
        .refine(
          (value) => {
            if (!value) return true;
            return value.length % 2 === 0;
          },
          {
            message:
              "answerFilters must be a list of question labels and values in sets of 2",
          },
        ),
      assigneeId: z.string().nullish(),
    }),
  ),
  params: z.void(),
  body: z.void(),
  output: paginatedOutput(IntakeSubmissionWithCustomColumnsSchema),
} as const satisfies QueryRoute;

export const GetIntakeSubmissionRoute = {
  method: "GET",
  path: "/api/intake-submission/:intakeSubmissionId",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.void(),
  output: GetIntakeSubmissionSchema,
} as const satisfies QueryRoute;

export const GetIntakeSubmissionAdditionalStepsRoute = {
  method: "GET",
  path: "/api/intake-submission/:intakeSubmissionId/additional-steps",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.void(),
  output: z
    .object({
      id: z.string(),
      label: z.string().nullable(),
      ordinal: z.number(),
      questions: z.array(
        z.object({
          id: z.string(),
          label: z.string(),
          ordinal: z.number(),
          answer: IntakeAnswer.nullable(),
        }),
      ),
    })
    .array(),
} as const satisfies QueryRoute;

export const GetIntakeSubmissionAssigneeRoute = {
  method: "GET",
  path: "/api/intake-submission/:intakeSubmissionId/assignee",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    assigneeIsEnabledForCategory: z.boolean(),
    isCurrentUserEligibleToBeAssignee: z.boolean(),
    isCurrentUserAssignee: z.boolean(),
    eligibleAssigneeGroups: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    ),
    assigneeUser: z
      .object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      })
      .nullable(),
  }),
} as const satisfies QueryRoute;

export const GetIntakeSubmissionViewersRoute = {
  method: "GET",
  path: "/api/intake-submission/:intakeSubmissionId/viewers",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.void(),
  output: z.array(IntakeViewerSchema),
} as const satisfies QueryRoute;

export const GetServiceFieldsFromDocumentRoute = {
  method: "GET",
  path: "/api/service-fields-from-document/:documentId",
  query: z.void(),
  params: z.object({
    documentId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    vendorName: z.string(),
    name: z.string(),
    description: z.string(),
    paymentFrequency: PaymentFrequencySchema,
    paymentTerm: PaymentTermsSchema,
    renewalDetails: z.string().nullable(),
    servicePeriods: z.array(
      z.object({
        startDate: z.string(),
        endDate: z.string().nullable(),
        unitsPerMonth: z.number(),
        costPerUnit: z.number(),
        tax: z.number(),
        totalCost: z.number(),
        note: z.string(),
      }),
    ),
  }),
} as const satisfies QueryRoute;

export const PostExtractDocumentTextRoute = {
  method: "POST",
  path: "/api/documents/:documentId/extractText",
  query: z.void(),
  params: z.object({
    documentId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    success: z.boolean(),
  }),
} as const satisfies MutationRoute;

export const GetIntakeSubmissionsNeedsAttentionRoute = {
  method: "GET",
  path: "/api/intake-submissions/needs-attention",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.array(IntakeSubmissionNeedsAttentionSchema),
} as const satisfies QueryRoute;

export const GetIntakeSubmissionAnswersRoute = {
  method: "GET",
  path: "/api/intake-submission/:intakeSubmissionId/answers",
  query: paginatedQuery(
    z.enum(["stepOrdinal", "questionOrdinal", "id"]),
    z.object({}),
  ),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.void(),
  output: paginatedOutput(IntakeAnswer),
} as const satisfies QueryRoute;

export const GetIntakeSubmissionTabsRoute = {
  method: "GET",
  path: "/api/intake-submission/:intakeSubmissionId/tabs",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.void(),
  output: GetIntakeSubmissionTabsOutput,
} as const satisfies QueryRoute;

export const PostIntakeSubmissionDuplicateRoute = {
  method: "POST",
  path: "/api/intake-submission/:id/duplicate",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.void(),
  output: z.object({
    id: z.string(),
  }),
} as const satisfies MutationRoute;

export const PostRestartIntakeSubmissionRoute = {
  method: "POST",
  path: "/api/intake-submission/:id/restart",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies MutationRoute;

export const PostIntakeSubmissionVendorsRoute = {
  method: "POST",
  path: "/api/intake-submission/:id/vendors",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.object({
    vendorIds: z.array(z.string()),
  }),
  output: z.void(),
} as const satisfies MutationRoute;

export const PostIntakeSubmissionServicesRoute = {
  method: "POST",
  path: "/api/intake-submission/:id/services",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.object({
    serviceIds: z.array(z.string()),
  }),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetIntakeSubmissionLinkedServicesRoute = {
  method: "GET",
  path: "/api/intake-submission/:id/linked-services",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.void(),
  output: GetIntakeSubmissionServicesFromAnswersOutput,
} as const satisfies QueryRoute;

export const GetIntakeSubmissionServicesFromAnswersRoute = {
  method: "GET",
  path: "/api/intake-submission/:id/services-from-answers",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.void(),
  output: GetIntakeSubmissionServicesFromAnswersOutput,
} as const satisfies QueryRoute;

export const DeleteIntakeSubmissionRoute = {
  method: "DELETE",
  path: "/api/intake-submission/:intakeSubmissionId",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies MutationRoute;

export const DeleteIntakeSubmissionServiceRoute = {
  method: "DELETE",
  path: "/api/intake-submission/:submissionId/services/:serviceId",
  query: z.void(),
  params: z.object({
    submissionId: z.string(),
    serviceId: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies MutationRoute;

export const DeleteIntakeSubmissionVendorRoute = {
  method: "DELETE",
  path: "/api/intake-submission/:submissionId/vendors/:vendorId",
  query: z.void(),
  params: z.object({
    submissionId: z.string(),
    vendorId: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetIntakeSubmissionDocumentsRoute = {
  method: "GET",
  path: "/api/intake-submission/:id/documents",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.void(),
  output: GetIntakeSubmissionDocumentsOutput,
} as const satisfies QueryRoute;

export const PostIntakeSubmissionDocumentsRoute = {
  method: "POST",
  path: "/api/intake-submission/:id/documents",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.object({ documentIds: z.array(z.string()) }),
  output: z.void(),
} as const satisfies MutationRoute;

export const DeleteIntakeSubmissionDocumentRoute = {
  method: "DELETE",
  path: "/api/intake-submission/:submissionId/documents/:documentId",
  query: z.void(),
  params: z.object({
    submissionId: z.string(),
    documentId: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetIntakeSubmissionThreadsRoute = {
  method: "GET",
  path: "/api/intake-submission/:intakeSubmissionId/threads",
  query: paginatedQuery(
    z.enum(["createdAt", "id"]),
    z.object({
      intakeSubmissionAnswerId: z.string().nullish(),
    }),
  ),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.void(),
  output: paginatedOutput(GetIntakeSubmissionThreadsOutput),
} as const satisfies QueryRoute;

export const GetIntakeSubmissionIroncladWorfklowValuesRoute = {
  method: "GET",
  path: "/api/intake-submission/:intakeSubmissionId/ironclad-workflow/:ironcladWorkflowId/values",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
    ironcladWorkflowId: z.string(),
  }),
  body: z.void(),
  output: z.array(IntakeSubmissionIroncladWorkflowValueSchema),
} as const satisfies QueryRoute;

export const GetLevelGroupStatusThreadsRoute = {
  method: "GET",
  path: "/api/level-group-status/:levelGroupStatusId/threads",
  query: paginatedQuery(z.enum(["createdAt", "id"]), z.object({})),
  params: z.object({
    levelGroupStatusId: z.string(),
  }),
  body: z.void(),
  output: paginatedOutput(GetIntakeSubmissionThreadsOutput),
} as const satisfies QueryRoute;

export const GetAllLevelGroupStatusThreadsRoute = {
  method: "GET",
  path: "/api/intake-submission/level-group-status/:intakeSubmissionId/threads",
  query: paginatedQuery(z.enum(["createdAt", "id"]), z.object({})),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.void(),
  output: paginatedOutput(GetLevelGroupStatusThreads),
} as const satisfies QueryRoute;

export const GetIntakeSubmissionMyApproverGroupsRoute = {
  method: "GET",
  path: "/api/intake-submission/:intakeSubmissionId/my-approver-groups",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    myApproverGroups: z.array(
      z.object({
        groupId: z.string().nullable(),
        groupName: z.string(),
        level: z.number(),
      }),
    ),
    requestApprovalMode: z.enum(["EACH_LEVEL_GROUP", "ALL_AT_ONCE"]),
  }),
} as const satisfies QueryRoute;

export const GetLevelGroupStatusHistoryRoute = {
  method: "GET",
  path: "/api/level-group-status/:levelGroupStatusId/history",
  query: z.void(),
  params: z.object({
    levelGroupStatusId: z.string(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      user: z
        .object({
          id: z.string(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string(),
        })
        .nullable(),
      createdAt: z.string().datetime(),
      blockStatus: BlockStatusSchema.nullish(),
      type: z.enum(["BLOCK_STATUS", "ASSIGNEE"]),
    }),
  ),
} as const satisfies QueryRoute;

export const GetIntakeSubmissionApprovalsRoute = {
  method: "GET",
  path: "/api/intake-submission/:intakeSubmissionId/approvals",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.void(),
  output: GetIntakeSubmissionApprovalStatusOutput,
} as const satisfies QueryRoute;

export const GetIntakeCategoriesRoute = {
  method: "GET",
  path: "/api/intake-categories",
  params: z.void(),
  query: paginatedQuery(
    z.enum(["createdAt", "id"]),
    z.object({
      type: IntakeCategoryType,
      includeOnlyCategoriesWithQuestions: z.enum(["true", "false"]).optional(),
      search: z.string().optional(),
    }),
  ),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string(),
      ordinal: z.number(),
      isReadOnly: z.boolean(),
      type: IntakeCategoryType,
      enableAssignees: z.boolean(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetIntakeCategoriesForSettingsRoute = {
  method: "GET",
  path: "/api/intake-categories/for-settings",
  params: z.void(),
  query: paginatedQuery(
    z.enum(["ordinal", "name"]),
    z.object({
      type: IntakeCategoryType,
      includeOnlyCategoriesWithQuestions: z.enum(["true", "false"]).optional(),
      search: z.string().optional(),
    }),
  ),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string(),
      ordinal: z.number(),
      isReadOnly: z.boolean(),
      type: IntakeCategoryType,
      enableAssignees: z.boolean(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetVendorServiceCustomColumnsRoute = {
  method: "GET",
  path: "/api/vendor-service-custom-columns",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.object({
    serviceColumns: z.array(
      z.object({
        id: z.string(),
        display: z.string(),
        ordinal: z.number(),
        intakeQuestionId: z.string().nullable(),
      }),
    ),
    vendorColumns: z.array(
      z.object({
        id: z.string(),
        display: z.string(),
        ordinal: z.number(),
        intakeQuestionId: z.string().nullable(),
      }),
    ),
  }),
} as const satisfies QueryRoute;

export const PostVendorServiceCustomColumnsRoute = {
  method: "POST",
  path: "/api/vendor-service-custom-columns",
  query: z.void(),
  params: z.void(),
  body: z.object({
    serviceColumns: z.array(
      z.object({
        id: z.string(),
        display: z.string(),
        ordinal: z.number(),
        intakeQuestionId: z.string().nullable(),
      }),
    ),
    vendorColumns: z.array(
      z.object({
        id: z.string(),
        display: z.string(),
        ordinal: z.number(),
        intakeQuestionId: z.string().nullable(),
      }),
    ),
  }),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetIntakeCategorySettingsRoute = {
  method: "GET",
  path: "/api/intake-category/:intakeCategoryId/settings",
  query: z.void(),
  params: z.object({
    intakeCategoryId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    dashboardConfig: z
      .object({
        id: z.string(),
        intakeCategoryId: z.string(),
        requestOwnerColumnQuestionId: z.string().nullable(),
        customColumns: z
          .object({
            id: z.string(),
            display: z.string(),
            ordinal: z.number(),
            questionId: z.string().nullable(),
            intakeWorkflowRuleId: z.string().nullable(),
          })
          .array(),
      })
      .nullable(),
    financialAnalysisConfig: z
      .object({
        isEnabledByDefault: z.boolean(),
        amountQuestionId: z.string().nullable(),
        startDateQuestionId: z.string().nullable(),
        endDateQuestionId: z.string().nullable(),
        vendorQuestionId: z.string().nullable(),
        categoryQuestionId: z.string().nullable(),
        departmentQuestionId: z.string().nullable(),
        businessUnitQuestionId: z.string().nullable(),
        entityQuestionId: z.string().nullable(),
      })
      .nullable(),
    enableAssignees: z.boolean(),
    assigneeGroups: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    ),
    enableOwners: z.boolean(),
    owners: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    ),
  }),
} as const satisfies QueryRoute;

export const PostIntakeCategoryDuplicateRoute = {
  method: "POST",
  path: "/api/intake-category/:intakeCategoryId/duplicate",
  query: z.void(),
  params: z.object({
    intakeCategoryId: z.string(),
  }),
  body: z.object({
    name: z.string(),
    includeForm: z.boolean(),
    includeWorkflow: z.boolean(),
    includeAdvanced: z.boolean(),
  }),
  output: z.object({
    id: z.string(),
  }),
} as const satisfies MutationRoute;

export const GetIntakeCategoryRulesRoute = {
  method: "GET",
  path: "/api/intake-category/:intakeCategoryId/rules",
  query: z.object({
    type: z.enum(["APPROVAL", "TASK"]).nullish(),
  }),
  params: z.object({
    intakeCategoryId: z.string(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      description: z.string(),
      label: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetIntakeSubmissionSpendRequestRoute = {
  method: "GET",
  path: "/api/intake-submission/:intakeSubmissionId/spend-request",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.void(),
  output: spendRequestForFinancialAnalysisSchema.nullable(),
} as const satisfies QueryRoute;

export const PostIntakeSubmissionSpendRequestRoute = {
  method: "POST",
  path: "/api/intake-submission/:intakeSubmissionId/spend-request",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.object({
    vendorId: z.string(),
    serviceStartDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    serviceEndDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullish(),
    lines: z.array(
      z.object({
        amount: z.number(),
        categoryId: z.string(),
        departmentId: z.string(),
        businessUnitId: z.string().nullish(),
        entityId: z.string().nullish(),
      }),
    ),
  }),
  output: z.void(),
} as const satisfies Route;

export const GetVendorVendorCompanyContactsRoute = {
  method: "GET",
  path: "/api/vendor/:vendorId/vendor-company-contacts",
  body: z.void(),
  query: z.void(),
  params: z.object({
    vendorId: z.string(),
  }),
  output: z.array(
    z.object({
      userId: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const GetVendorFormSubmissionsRoute = {
  method: "GET",
  path: "/api/vendor-form-submissions",
  query: paginatedQuery(
    z.enum(["id", "createdAt"]),
    z.object({
      filter: z.enum(["approver", "watcher", "recently-completed"]).nullish(),
    }),
  ),
  params: z.void(),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      id: z.string(),
      title: z.string().nullable(),
      number: z.number(),
      isDraft: z.boolean(),
      archived: z.boolean(),
      createdAt: z.string(),
      status: RequestStatusSchema,
      progress: z.number(),
      fromUser: z
        .object({
          id: z.string(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string(),
        })
        .nullable(),
      createdBy: z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      }),
      vendor: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      intakeCategory: z.object({
        id: z.string(),
        name: z.string(),
        type: IntakeCategoryType,
      }),
    }),
  ),
} as const satisfies Route;

export const GetVendorFormSubmissionsCsvRoute = {
  method: "GET",
  path: "/api/vendor-form-submissions.csv",
  params: z.void(),
  body: z.void(),
  query: paginatedQuery(
    z.enum(["createdAt", "id", "status", "requestorName", "number"]),
    z.object({
      isDraft: z.union([z.literal("true"), z.literal("false")]).optional(),
      intakeCategoryIds: z.string().array().optional(),
      startYearMonthDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      endYearMonthDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      isAssignedToUser: z
        .union([z.literal("true"), z.literal("false")])
        .nullish(),
      needsAttention: z
        .union([z.literal("true"), z.literal("false")])
        .nullish(),
      search: z.string().optional(),
      answerFilters: z.array(z.string()).nullish(),
    }),
  ),
  output: z.void(),
  stream: true,
} as const satisfies Route;

export const GetVendorFormsRoute = {
  method: "GET",
  path: "/api/vendor/:vendorId/forms",
  query: paginatedQuery(z.enum(["createdAt", "id"]), z.object({})),
  params: z.object({
    vendorId: z.string(),
  }),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      id: z.string(),
      createdAt: z.string(),
      sentBy: z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      }),
      intakeCategoryLinks: z.array(
        z.object({
          intakeCategory: z.object({
            id: z.string(),
            name: z.string(),
            type: IntakeCategoryType,
          }),
          intakeSubmission: z.object({
            id: z.string(),
            createdAt: z.string().datetime(),
            updatedAt: z.string().datetime(),
            status: RequestStatusSchema,
            isDraft: z.boolean(),
            archived: z.boolean(),
            title: z.string().nullable(),
            number: z.number(),
          }),
        }),
      ),
    }),
  ),
} as const satisfies Route;

export const GetVendorIntakeAnswersRoute = {
  method: "GET",
  path: "/api/vendor/:vendorId/intake-answers",
  query: z.void(),
  params: z.object({
    vendorId: z.string(),
  }),
  body: z.void(),
  output: z.array(IntakeAnswer),
} as const satisfies Route;

export const GetIntakeCategoryStepsRoute = {
  method: "GET",
  path: "/api/intake-category/:intakeCategoryId/steps",
  query: paginatedQuery(
    z.enum(["id"]),
    z.object({
      includeAdditionalSteps: z
        .enum(["true", "false"])
        .optional()
        .default("false"),
    }),
  ),
  params: z.object({
    intakeCategoryId: z.string(),
  }),
  body: z.void(),
  output: paginatedOutput(IntakeQuestionSchema),
} as const satisfies Route;

export const GetIntakeCategoryStepsBareRoute = {
  method: "GET",
  path: "/api/intake-category/:intakeCategoryId/steps/bare",
  query: z.object({
    types: z.enum(["NORMAL", "ADDITIONAL"]).array(),
  }),
  params: z.object({
    intakeCategoryId: z.string(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      ordinal: z.number(),
      label: z.string().nullable(),
    }),
  ),
} as const satisfies Route;

export const GetPublicIntakeCategoryStepsRoute = {
  method: "GET",
  path: "/public-api/intake-category/:intakeCategoryId/steps",
  query: paginatedQuery(
    z.enum(["id"]),
    z.object({
      includeAdditionalSteps: z
        .enum(["true", "false"])
        .optional()
        .default("false"),
    }),
  ),
  params: z.object({
    intakeCategoryId: z.string(),
  }),
  body: z.void(),
  output: paginatedOutput(IntakeQuestionSchema),
} as const satisfies PublicQueryRoute;

export const GetIntakeCategoryStepsCsvRoute = {
  method: "GET",
  path: "/api/intake-category/:intakeCategoryId/steps.csv",
  query: z.object({}),
  params: z.object({ intakeCategoryId: z.string() }),
  body: z.void(),
  output: z.void(),
  stream: true,
} as const satisfies Route;

export const GetCompanyLogoSignedUrlRoute = {
  method: "GET",
  path: "/api/company-logo-signed-url",
  params: z.void(),
  query: z.void(),
  body: z.void(),
  output: z.object({
    lightLogoUrl: z.string().nullable(),
    darkLogoUrl: z.string().nullable(),
  }),
} as const satisfies Route;

export const GetCompanyLogoPublicUrlRoute = {
  method: "GET",
  path: "/public-api/company-logo-public-url/:companyId",
  params: z.object({ companyId: z.string() }),
  query: z.void(),
  body: z.void(),
  output: z.object({
    lightLogoUrl: z.string().nullable(),
    darkLogoUrl: z.string().nullable(),
  }),
} as const satisfies PublicQueryRoute;

export const PostIntakeSubmissionStatusRoute = {
  method: "POST",
  path: "/api/intake-submission/:intakeSubmissionId/status",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: PostIntakeSubmissionApprovalInputSchema,
  output: z.void(),
} as const satisfies Route;

export const PostIntakeSubmissionCompleteTaskRoute = {
  method: "POST",
  path: "/api/intake-submission/:intakeSubmissionId/complete-task",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.object({ comment: z.string(), levelGroupStatusId: z.string() }),
  output: z.void(),
} as const satisfies Route;

export const PostIntakeSubmissionBypassRoute = {
  method: "POST",
  path: "/api/intake-submission/:intakeSubmissionId/bypass",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.object({
    levelGroupStatusId: z.string(),
    approverNote: z.string(),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostIntakeSubmissionRemindRoute = {
  method: "POST",
  path: "/api/intake-submission/:intakeSubmissionId/remind",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.object({
    levelGroupStatusId: z.string(),
    note: z.string(),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostIntakeSubmissionThreadsRoute = {
  method: "POST",
  path: "/api/intake-submission/:intakeSubmissionId/threads",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.object({
    commentBody: z.string(),
    documentIds: z.array(z.string()).optional(),
    intakeSubmissionAnswerId: z.string().nullish(),
    shareWithVendor: z.boolean().nullish(),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostLevelGroupStatusDocuments = {
  method: "POST",
  path: "/api/level-group-status/:levelGroupStatusId/documents",
  query: z.void(),
  params: z.object({
    levelGroupStatusId: z.string(),
  }),
  body: z.object({
    documentIds: z.array(z.string()),
  }),
  output: z.void(),
} as const satisfies MutationRoute;

export const PostBillLevelGroupStatusBypassRoute = {
  method: "POST",
  path: "/api/bill-level-group-status/:levelGroupStatusId/bypass",
  query: z.void(),
  params: z.object({
    levelGroupStatusId: z.string(),
  }),
  body: z.object({
    approverNote: z.string(),
  }),
  output: z.void(),
} as const satisfies MutationRoute;

export const GetLevelGroupStatusDocuments = {
  method: "GET",
  path: "/api/level-group-status/:levelGroupStatusId/documents",
  query: z.void(),
  params: z.object({
    levelGroupStatusId: z.string(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      displayName: z.string(),
    }),
  ),
} as const satisfies QueryRoute;

export const PostLevelGroupStatusThreadsRoute = {
  method: "POST",
  path: "/api/level-group-status/:levelGroupStatusId/threads",
  query: z.void(),
  params: z.object({
    levelGroupStatusId: z.string(),
  }),
  body: z.object({
    commentBody: z.string(),
    documentIds: z.array(z.string()),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostLevelGroupStatusBlockStatusRoute = {
  method: "POST",
  path: "/api/level-group-status/:levelGroupStatusId/block-status",
  query: z.void(),
  params: z.object({
    levelGroupStatusId: z.string(),
  }),
  body: z.object({
    blockStatus: BlockStatusSchema,
  }),
  output: z.void(),
} as const satisfies Route;

export const PostLevelGroupStatusAssigneeRoute = {
  method: "POST",
  path: "/api/level-group-status/:levelGroupStatusId/assignee",
  query: z.void(),
  params: z.object({
    levelGroupStatusId: z.string(),
  }),
  body: z.object({
    assigneeUserId: z.string().nullable(),
  }),
  output: z.void(),
} as const satisfies Route;

export const PutIntakeSubmissionArchivedRoute = {
  method: "PUT",
  path: "/api/intake-submission/:intakeSubmissionId/archived",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.object({ archived: z.boolean() }),
  output: z.void(),
} as const satisfies Route;

export const PostIntakeSubmissionWatchersRoute = {
  method: "POST",
  path: "/api/intake-submission/:intakeSubmissionId/watchers",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.object({ watcherIds: z.array(z.string()) }),
  output: z.void(),
} as const satisfies Route;

export const PostMembersPermissionsRoute = {
  method: "POST",
  path: "/api/members/permissions",
  query: z.void(),
  params: z.void(),
  body: z.object({
    memberIds: z.array(z.string()),
    hasAdminAccess: z.boolean(),
    hasSuperViewerAccess: z.boolean(),
    hasDepartmentAccess: z.boolean(),
    hasDepartmentPayrollAccess: z.boolean(),
    hasBusinessUnitAccess: z.boolean(),
    hasBusinessUnitPayrollAccess: z.boolean(),
    hasPaymentsAccess: z.boolean(),
    hasFinancialAnalysisAccess: z.boolean(),
    approvesRequestsForDepartment: z.boolean(),
    approvesRequestsForBusinessUnit: z.boolean(),
    submitsSpendRequestsForOthers: z.boolean(),
    isSigner: z.boolean(),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostMembersGroupsRoute = {
  method: "POST",
  path: "/api/members/groups",
  query: z.void(),
  params: z.void(),
  body: z.object({
    memberIds: z.array(z.string()),
    groupIds: z.array(z.string()),
    mode: z.enum(["add", "replace"]),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostMembersDepartmentsRoute = {
  method: "POST",
  path: "/api/members/departments",
  query: z.void(),
  params: z.void(),
  body: z.object({
    memberIds: z.array(z.string()),
    departmentIds: z.array(z.string()),
    mode: z.enum(["add", "replace"]),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostMembersBusinessUnitsRoute = {
  method: "POST",
  path: "/api/members/business-units",
  query: z.void(),
  params: z.void(),
  body: z.object({
    memberIds: z.array(z.string()),
    businessUnitIds: z.array(z.string()),
    mode: z.enum(["add", "replace"]),
  }),
  output: z.void(),
} as const satisfies Route;

export const GetVendorsWithLast12SpendRoute = {
  method: "GET",
  path: "/api/vendors-with-last12-spend",
  query: paginatedQuery(
    z.enum(["last12Spend", "name", "ownedDocuments", "ownedServices"]),
    z.object({
      name: z.string().nullish(),
      ownerId: z.string().nullish(),
      spendCategoryId: z.string().nullish(),
      departmentId: z.string().nullish(),
      archived: z.enum(["true", "false"]).nullish(),
      answerFilters: z
        .array(z.string())
        .nullish()
        .refine(
          (value) => {
            if (!value) return true;
            return value.length % 2 === 0;
          },
          {
            message:
              "answerFilters must be a list of question labels and values in sets of 2",
          },
        ),
    }),
  ),
  params: z.void(),
  body: z.void(),
  output: paginatedOutput(VendorWithLast12Schema),
} as const satisfies Route;

export const GetVendorsWithLast12SpendCsvRoute = {
  method: "GET",
  path: "/api/vendors-with-last12-spend.csv",
  query: paginatedQuery(
    z.enum(["last12Spend", "name"]),
    z.object({
      name: z.string().nullish(),
      ownerId: z.string().nullish(),
      spendCategoryId: z.string().nullish(),
      departmentId: z.string().nullish(),
      answerFilters: z
        .array(z.string())
        .nullish()
        .refine(
          (value) => {
            if (!value) return true;
            return value.length % 2 === 0;
          },
          {
            message:
              "answerFilters must be a list of question labels and values in sets of 2",
          },
        ),
    }),
  ),
  params: z.void(),
  body: z.void(),
  output: z.void(),
  stream: true,
} as const satisfies Route;

export const PostVendorRoute = {
  method: "POST",
  path: "/api/vendors",
  query: z.void(),
  params: z.void(),
  body: z.object({
    name: z.string(),
    paymentMethod: PaymentMethodSchema,
    ownerIds: z.array(z.string()),
    signerId: z.string().optional(),
    description: z.string(),
    externalPointOfContact: z.string().optional(),
    externalSigner: z.string().optional(),
    documentIds: z.array(z.string()),
    erpVendorIds: z.array(z.string()),
    isSocTwoCompliant: z.boolean().optional(),
    customFields: z.array(CreateIntakeSubmissionFieldSchema),
    taxPayerIdentificationNumbers: z.array(
      z.object({
        id: z.string(),
        taxPayerIdentificationNumber: z.string(),
      }),
    ),
  }),
  output: z.object({
    id: z.string(),
  }),
} as const satisfies Route;

export const GetVendorRoute = {
  method: "GET",
  path: "/api/vendors/:vendorId",
  query: z.void(),
  params: z.object({ vendorId: z.string() }),
  body: z.void(),
  output: z
    .object({
      id: z.string(),
      name: z.string(),
      description: z.string().nullable(),
      createdAt: z.string().datetime(),
      updatedAt: z.string().datetime(),
      paymentMethod: PaymentMethodSchema,
      externalPointOfContact: z.string().nullable(),
      externalSigner: z.string().nullable(),
      isSocTwoCompliant: z.boolean().nullable(),
      archived: z.boolean(),
      owners: z.array(
        z.object({
          id: z.string(),
          email: z.string(),
          firstName: z.string(),
          lastName: z.string(),
        }),
      ),
      signer: z
        .object({
          id: z.string(),
          email: z.string(),
          firstName: z.string(),
          lastName: z.string(),
        })
        .nullable(),
      ownedDocuments: z.array(
        z.object({
          id: z.string(),
          displayName: z.string(),
        }),
      ),
      vendorTaxPayerIdentificationNumbers: z.array(
        z.object({
          id: z.string(),
          taxPayerIdentificationNumber: z.string(),
        }),
      ),
    })
    .nullable(),
} as const satisfies Route;

export const PutVendorRoute = {
  method: "PUT",
  path: "/api/vendors/:vendorId",
  query: z.void(),
  params: z.object({
    vendorId: z.string(),
  }),
  body: z.object({
    name: z.string(),
    paymentMethod: PaymentMethodSchema,
    ownerIds: z.array(z.string()),
    signerId: z.string().optional(),
    description: z.string(),
    externalPointOfContact: z.string().optional(),
    externalSigner: z.string().optional(),
    documentIds: z.array(z.string()),
    erpVendorIds: z.array(z.string()),
    isSocTwoCompliant: z.boolean().optional(),
    customFields: z.array(CreateIntakeSubmissionFieldSchema),
    vendorTaxPayerIdentificationNumbers: z.array(
      z.object({
        id: z.string(),
        taxPayerIdentificationNumber: z.string(),
      }),
    ),
  }),
  output: z.void(),
} as const satisfies Route;

export const GetVendorsRoute = {
  method: "GET",
  path: "/api/vendors",
  query: paginatedQuery(
    z.enum(["name", "createdAt"]),
    z.object({
      vendorIds: z.string().array().nullish(),
    }),
  ),
  params: z.void(),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
} as const satisfies Route;

export const GetVendorOwnersRoute = {
  method: "GET",
  path: "/api/vendors/:vendorId/owners",
  query: z.void(),
  params: z.object({
    vendorId: z.string(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    }),
  ),
} as const satisfies Route;

export const GetVendorCustomFieldsRoute = {
  method: "GET",
  path: "/api/vendors/:vendorId/custom-fields",
  query: z.void(),
  params: z.object({
    vendorId: z.string(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      intakeQuestion: z.object({
        id: z.string(),
        label: z.string(),
        ordinal: z.number(),
        isRequired: z.boolean(),
      }),
      answer: IntakeAnswer.nullable(),
    }),
  ),
} as const satisfies Route;

export const GetPlanVendorsRoute = {
  method: "GET",
  path: "/api/plan-vendors",
  query: paginatedQuery(
    z.enum(["name", "createdAt"]),
    z.object({
      planVendorIds: z.string().array().nullish(),
      planId: z.string().optional(),
    }),
  ),
  params: z.void(),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
} as const satisfies Route;

export const PostVendorsByNameRoute = {
  method: "POST",
  path: "/api/vendors-by-name",
  query: z.void(),
  params: z.void(),
  body: z.object({
    name: z.string(),
  }),
  output: z.object({
    id: z.string(),
  }),
} as const satisfies Route;

export const PostArchiveVendorsRoute = {
  method: "POST",
  path: "/api/vendors/archive",
  query: z.void(),
  params: z.void(),
  body: z.object({
    vendorIds: z.array(z.string()),
    archived: z.boolean(),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostSetVendorPaymentMethodsRoute = {
  method: "POST",
  path: "/api/vendors/set-payment-methods",
  query: z.void(),
  params: z.void(),
  body: z.object({
    vendorIds: z.array(z.string()),
    paymentMethod: PaymentMethodSchema,
  }),
  output: z.object({
    success: z.boolean(),
  }),
} as const satisfies Route;

export const PostSetVendorOwnersRoute = {
  method: "POST",
  path: "/api/vendors/set-owners",
  query: z.void(),
  params: z.void(),
  body: z.object({
    vendorIds: z.array(z.string()),
    ownerId: z.string(),
  }),
  output: z.object({
    success: z.boolean(),
  }),
} as const satisfies Route;

export const PostServicesRoute = {
  method: "POST",
  path: "/api/services",
  query: z.void(),
  params: z.void(),
  body: z.object({
    name: z.string(),
    description: z.string().nullish(),
    ownerIds: z.string().array(),
    departmentIds: z.string().array(),
    locationIds: z.string().array(),
    vendorId: z.string().nullish(),
    newVendorName: z.string().nullish(),
    spendCategoryId: z.string().nullish(),
    businessUnitId: z.string().nullish(),
    paymentFrequency: PaymentFrequencySchema.nullish(),
    paymentTerm: PaymentTermsSchema.nullish(),
    spendRequestIds: z.string().array(),
    autorenews: z.boolean(),
    autoRenewalDetail: z.string().nullish(),
    documentIds: z.string().array(),
    servicePeriods: z.array(
      z.object({
        startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        endDate: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}$/)
          .nullish(),
        unitsPerMonth: z.number().nullish(),
        costPerUnit: z.number().nullish(),
        tax: z.number().nullish(),
        totalServicePeriodCost: z.number(),
        note: z.string().nullish(),
        servicePeriodRenewalReminders: z.array(
          z.object({
            renewalReminderDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
          }),
        ),
        customFields: z.array(CreateIntakeSubmissionFieldSchema),
        currency: z.string().nullish(),
      }),
    ),
    customFields: z.array(CreateIntakeSubmissionFieldSchema),
  }),
  output: z.object({
    id: z.string(),
    name: z.string(),
    vendorId: z.string(),
  }),
} as const satisfies Route;

export const PostLevelGroupStatusCreateJiraIssueRoute = {
  method: "POST",
  path: "/api/level-group-statuses/:levelGroupStatusId/create-jira-issue",
  query: z.void(),
  params: z.object({
    levelGroupStatusId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    success: z.boolean(),
  }),
} as const satisfies MutationRoute;

export const PostResyncLevelGroupStatusCreateJiraIssueRoute = {
  method: "POST",
  path: "/api/level-group-statuses/:levelGroupStatusId/resync-create-jira-issue",
  query: z.void(),
  params: z.object({
    levelGroupStatusId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    changedStatus: z.boolean(),
    statusCategory: z.string(),
  }),
} as const satisfies MutationRoute;

export const PutServicesRoute = {
  method: "PUT",
  path: "/api/services/:serviceId",
  query: z.void(),
  params: z.object({
    serviceId: z.string(),
  }),
  body: z.object({
    name: z.string(),
    description: z.string().nullish(),
    ownerIds: z.string().array(),
    departmentIds: z.string().array(),
    locationIds: z.string().array(),
    vendorId: z.string().nullish(),
    newVendorName: z.string().nullish(),
    spendCategoryId: z.string().nullish(),
    businessUnitId: z.string().nullish(),
    paymentFrequency: PaymentFrequencySchema.nullish(),
    paymentTerm: PaymentTermsSchema.nullish(),
    spendRequestIds: z.string().array(),
    autorenews: z.boolean(),
    autoRenewalDetail: z.string().nullish(),
    documentIds: z.string().array(),
    servicePeriods: z.array(
      z.object({
        id: z.string().optional(),
        startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        endDate: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}$/)
          .nullish(),
        unitsPerMonth: z.number().nullish(),
        costPerUnit: z.number().nullish(),
        tax: z.number().nullish(),
        totalServicePeriodCost: z.number(),
        note: z.string().nullish(),
        servicePeriodRenewalReminders: z.array(
          z.object({
            renewalReminderDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
          }),
        ),
        customFields: z.array(CreateIntakeSubmissionFieldSchema),
        currency: z.string().nullish(),
      }),
    ),
    customFields: z.array(CreateIntakeSubmissionFieldSchema),
  }),
  output: z.object({
    id: z.string(),
    name: z.string(),
    vendorId: z.string(),
  }),
} as const satisfies Route;

export const GetServicesRoute = {
  method: "GET",
  path: "/api/services",
  query: paginatedQuery(
    z.enum(["id", "createdAt"]),
    z.object({
      vendorId: z.string().nullish(),
      spendRequestId: z.string().nullish(),
      archived: z.enum(["true", "false"]).nullish(),
      name: z.string().nullish(),
      answerFilters: z
        .array(z.string())
        .nullish()
        .refine(
          (value) => {
            if (!value) return true;
            return value.length % 2 === 0;
          },
          {
            message:
              "answerFilters must be a list of question labels and values in sets of 2",
          },
        ),
    }),
  ),
  params: z.void(),
  body: z.void(),
  output: paginatedOutput(ServiceListingSchema),
} as const satisfies Route;

export const GetServiceRoute = {
  method: "GET",
  path: "/api/services/:serviceId",
  query: z.void(),
  params: z.object({
    serviceId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    id: z.string(),
    name: z.string(),
    archived: z.boolean(),
    description: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    owners: z
      .object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      })
      .array(),
    spendCategory: z
      .object({
        id: z.string(),
        name: z.string(),
      })
      .nullable(),
    paymentFrequency: PaymentFrequencySchema.nullable(),
    paymentTerm: PaymentTermsSchema,
    autorenews: z.boolean(),
    autoRenewalDetail: z.string().nullable(),
    vendor: z.object({
      id: z.string(),
      name: z.string(),
      archived: z.boolean(),
    }),
    departments: z
      .object({
        id: z.string(),
        name: z.string(),
      })
      .array(),
    businessUnit: z
      .object({
        id: z.string(),
        name: z.string(),
      })
      .nullable(),
    locations: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    ),
    relatedDocuments: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        displayName: z.string(),
        type: z.array(DocumentTypeSchema),
        createdAt: z.string().datetime(),
        updatedAt: z.string().datetime(),
        mimeType: z.string(),
      }),
    ),
    servicePeriods: z.array(
      z.object({
        id: z.string(),
        startDate: z.string(),
        endDate: z.string().nullable(),
        currency: z.string().nullable(),
        costPerUnit: z.number().nullable(),
        unitsPerMonth: z.number().nullable(),
        totalServicePeriodCost: z.number(),
        tax: z.number().nullable(),
        note: z.string().nullable(),
        servicePeriodRenewalReminders: z.array(
          z.object({
            id: z.string(),
            renewalReminderDate: z.string(),
          }),
        ),
      }),
    ),
    spendRequests: z.array(
      z.object({
        id: z.string(),
        readableId: z.string(),
        vendor: z.object({
          id: z.string(),
          name: z.string(),
        }),
        estimatedCost: z.number(),
        createdAt: z.string().datetime(),
        updatedAt: z.string().datetime(),
        categories: z.array(z.object({ name: z.string() })),
      }),
    ),
  }),
} as const satisfies Route;

export const GetServiceCustomFieldsRoute = {
  method: "GET",
  path: "/api/services/:serviceId/custom-fields",
  query: z.void(),
  params: z.object({
    serviceId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    serviceCustomFields: z.array(
      z.object({
        id: z.string(),
        intakeQuestion: z.object({
          id: z.string(),
          label: z.string(),
          ordinal: z.number(),
          isRequired: z.boolean(),
        }),
        answer: IntakeAnswer.nullable(),
      }),
    ),
    servicePeriodCustomFields: z.record(
      z.string(),
      z.array(
        z.object({
          id: z.string(),
          intakeQuestion: z.object({
            id: z.string(),
            label: z.string(),
            ordinal: z.number(),
            isRequired: z.boolean(),
          }),
          answer: IntakeAnswer.nullable(),
        }),
      ),
    ),
  }),
} as const satisfies Route;

export const PostServiceArchiveRoute = {
  method: "POST",
  path: "/api/services/archive",
  query: z.void(),
  params: z.void(),
  body: z.object({
    serviceIds: z.array(z.string()),
    archived: z.boolean(),
  }),
  output: z.void(),
} as const satisfies Route;

export const GetServiceRenewalReportRoute = {
  method: "GET",
  path: "/api/services/renewal-report",
  query: z.object({
    startDateFrom: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullish(),
    startDateTo: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullish(),
    endDateFrom: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullish(),
    endDateTo: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullish(),
  }),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      serviceId: z.string(),
      servicePeriodId: z.string(),
      name: z.string(),
      servicePeriodCost: z.number(),
      startDate: z.string(),
      endDate: z.string().nullable(),
      owners: z
        .object({
          id: z.string(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string(),
          departments: z.array(
            z.object({
              id: z.string(),
              name: z.string(),
            }),
          ),
        })
        .array(),
      vendor: z.object({
        id: z.string(),
        name: z.string(),
      }),
    }),
  ),
} as const satisfies Route;

export const PostIntakeCategorySettingsRoute = {
  method: "POST",
  path: "/api/intake-category/:intakeCategoryId/settings",
  query: z.void(),
  params: z.object({
    intakeCategoryId: z.string(),
  }),
  body: z.object({
    dashboardConfig: z.object({
      requestOwnerColumnQuestionId: z.string().nullish(),
      customColumns: z
        .object({
          id: z.string(),
          display: z.string(),
          ordinal: z.number(),
          questionId: z.string().nullish(),
          taskRuleId: z.string().nullish(),
        })
        .refine(
          (data) => !!data.questionId || !!data.taskRuleId,
          "Must provide either a question or a task",
        )
        .array(),
    }),
    financialAnalysisConfig: z.object({
      isEnabledByDefault: z.boolean(),
      amountQuestionId: z.string().nullish(),
      startDateQuestionId: z.string().nullish(),
      endDateQuestionId: z.string().nullish(),
      vendorQuestionId: z.string().nullish(),
      categoryQuestionId: z.string().nullish(),
      departmentQuestionId: z.string().nullish(),
      businessUnitQuestionId: z.string().nullish(),
      entityQuestionId: z.string().nullish(),
    }),
    enableAssignees: z.boolean(),
    assigneeGroupIds: z.array(z.string()),
    enableOwners: z.boolean(),
    owners: z.array(z.string()),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostIntakeSubmissionRoute = {
  method: "POST",
  path: "/api/intake-submission",
  query: z.void(),
  params: z.void(),
  body: z.object({
    batch: z.number(),
    totalBatches: z.number(),
    submission: CreateIntakeSubmissionSchema,
  }),
  output: z.object({
    intakeSubmissionId: z.string(),
    isDraft: z.boolean(),
  }),
} as const satisfies Route;

export const PostPublicIntakeSubmissionRoute = {
  method: "POST",
  path: "/public-api/intake-submission",
  query: z.void(),
  params: z.void(),
  body: z.object({
    email: z.string().email(),
    batch: z.number(),
    totalBatches: z.number(),
    submission: CreateIntakeSubmissionSchema,
    companyId: z.string(),
  }),
  output: z.object({
    intakeSubmissionId: z.string(),
    isDraft: z.boolean(),
  }),
} as const satisfies PublicMutationRoute;

export const PostIntakeSubmissionAssigneeRoute = {
  method: "POST",
  path: "/api/intake-submission/:intakeSubmissionId/assignee",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.object({
    userId: z.string().nullish(),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostIntakeSubmissionTaskRoute = {
  method: "POST",
  path: "/api/intake-submission/:intakeSubmissionId/task",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.object({
    lgsType: IntakeWorkflowRuleTypeSchema.extract([
      "APPROVAL",
      "ADDITIONAL_STEP",
      "SEND_VENDOR_FORM",
      "TASK",
    ]),
    label: z.string(),
    description: z.string(),
    approverStatusType: RequestApproverTypeSchema,
    groupIds: z.string().array(),
    level: z.number(),
    additionalStepIds: z.string().array().nullish(),
    vendorIntakeCategoryIds: z.string().array().nullish(),
    vendorId: z.string().nullish(),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostIntakeSubmissionSpecialRequestRoute = {
  method: "POST",
  path: "/api/intake-submission/:intakeSubmissionId/special-request",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.object({
    vendorId: z.string(),
    serviceStartDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    serviceEndDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullish(),
    lines: z.array(
      z.object({
        amount: z.number(),
        categoryId: z.string(),
        departmentId: z.string(),
        businessUnitId: z.string().nullish(),
      }),
    ),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostVendorSendIntakeInvitationRoute = {
  method: "POST",
  path: "/api/vendor/:vendorId/send-intake-invitation",
  query: z.void(),
  params: z.object({
    vendorId: z.string(),
  }),
  body: z
    .object({
      email: z.string().email().nullish(),
      ccIds: z.string().array(),
      isForAllVendorCompanyUsers: z.boolean(),
      vendorUserId: z.string().nullish(),
      intakeCategoryIds: z.string().array(),
      message: z.string(),
      levelGroupStatusSendVendorFormTaskId: z.string().optional(),
    })
    .refine(
      (data) =>
        !!data.email ||
        !!data.isForAllVendorCompanyUsers ||
        !!data.vendorUserId,
      {
        message:
          "Must provide either an email, a vendor user, or address it to all vendor company users.",
      },
    ),
  output: z.void(),
} as const satisfies Route;

export const PostSendDocumentsRoute = {
  method: "POST",
  path: "/api/send-documents",
  query: z.void(),
  params: z.void(),
  body: z.object({
    documentIds: z.string().array(),
    levelGroupStatusSendDocumentTaskId: z.string().optional(),
    intakeSubmissionId: z.string().optional(),
    email: z.string().email().nullish(),
    message: z.string().nullish(),
  }),
  output: z.void(),
} as const satisfies Route;

export const DeleteVendorFormsDeleteSubmissionRoute = {
  method: "DELETE",
  path: "/api/vendor-forms/delete-submission",
  query: z.void(),
  params: z.void(),
  body: z.object({
    submissionId: z.string(),
  }),
  output: z.void(),
} as const satisfies Route;

export const GetPaymentsBillPaymentRoute = {
  method: "GET",
  path: "/api/payments/bill-payments/:id",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.void(),
  output: BillPaymentSchema,
} as const satisfies Route;

export const GetPaymentsBillLineItemsReportCsvRoute = {
  method: "GET",
  path: "/api/payments/bills-line-items-report.csv",
  query: z.object({
    status: z.enum(["APPROVED", "PENDING", "REJECTED"]).nullish(),
  }),
  params: z.void(),
  body: z.void(),
  output: z.void(),
  stream: true,
} as const satisfies Route;

export const GetPaymentsBillsLineItemsReportRoute = {
  method: "GET",
  path: "/api/payments/bills-line-items-report",
  query: paginatedQuery(
    z.enum(["invoiceDate", "billId", "billLineId"]),
    z.object({
      status: z.enum(["APPROVED", "PENDING", "REJECTED"]).nullish(),
    }),
  ),
  params: z.void(),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      billId: z.string(),
      billLineId: z.string(),
      invoiceNumber: z.string(),
      intakeSubmission: z
        .object({
          id: z.string(),
          number: z.number(),
          title: z.string(),
          version: z.number(),
          intakeCategory: z.object({
            id: z.string(),
            name: z.string(),
            type: IntakeCategoryType,
          }),
        })
        .nullable(),
      spendRequest: z.string().nullable(),
      erpVendor: z.object({
        id: z.string(),
        name: z.string(),
      }),
      invoiceDate: z.string(),
      dueDate: z.string(),
      createdBy: z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      }),
      postingPeriod: z.string().nullable(),
      description: z.string(),
      memo: z.string().nullable(),
      entity: z.string(),
      amount: z.number(),
      glAccount: z.string(),
      erpDepartment: z.string().nullable(),
      erpBusinessUnit: z.string().nullable(),
    }),
  ),
} as const satisfies Route;

export const GetPaymentsBillsNeedsAttentionRoute = {
  method: "GET",
  path: "/api/payments/bills-needs-attention",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      dueDate: z.string(),
      totalAmount: z.number(),
      createdAt: z.string(),
      createdBy: z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      }),
      invoiceNumber: z.string(),
      erpVendor: z.object({
        name: z.string(),
      }),
      asDelegateFor: z
        .object({
          id: z.string(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string(),
        })
        .nullable(),
    }),
  ),
} as const satisfies Route;

export const GetPaymentsBillsRoute = {
  method: "GET",
  path: "/api/payments/bills",
  query: paginatedQuery(
    z.enum(["createdAt", "id"]),
    z.object({
      vendorId: z.string().nullish(),
      intakeSubmissionId: z.string().nullish(),
      documentId: z.string().nullish(),
    }),
  ),
  params: z.void(),
  body: z.void(),
  output: paginatedOutput(BillSchema),
} as const satisfies Route;

export const GetPaymentsBillPaymentsRoute = {
  method: "GET",
  path: "/api/payments/bill-payments",
  query: paginatedQuery(
    z.enum(["id", "createdAt", "totalAmount"]),
    z.object({
      search: z.string().nullish(),
    }),
  ),
  params: z.void(),
  body: z.void(),
  output: paginatedOutput(BillPaymentSchema),
} as const satisfies Route;

export const GetPaymentsBillRoute = {
  method: "GET",
  path: "/api/payments/bills/:id",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.void(),
  output: BillSchema,
} as const satisfies Route;

export const DeletePaymentsBillRoute = {
  method: "DELETE",
  path: "/api/payments/bills/:id",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies Route;

export const PostUpdateDocumentsOnBillRoute = {
  method: "POST",
  path: "/api/payments/bills/:billId/documents/update",
  query: z.void(),
  params: z.object({
    billId: z.string(),
  }),
  body: z.object({
    documentIds: z.string().array(),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostPaymentsBillAddApprovalRoute = {
  method: "POST",
  path: "/api/payments/bills/:billId/add-approval",
  query: z.void(),
  params: z.object({
    billId: z.string(),
  }),
  body: z.object({
    approverStatusType: RequestApproverTypeSchema,
    groupIds: z.string().array(),
    level: z.number(),
  }),
  output: z.void(),
} as const satisfies Route;

export const PutPaymentsBillRoute = {
  method: "PUT",
  path: "/api/payments/bills/:billId",
  query: z.void(),
  params: z.object({
    billId: z.string(),
  }),
  body: z.object({
    spendRequestId: z.string().nullish(),
    intakeSubmissionId: z.string().nullish(),
    erpVendorId: z.string().nullable(),
    newErpVendorName: z.string().nullable(),
    newErpVendorEmail: z.string().nullable(),
    shouldCreateSudoziVendor: z.boolean(),
    invoiceNumber: z.string(),
    createdById: z.string().nullish(),
    invoiceId: z.string(),
    invoiceDate: z.string(),
    dueDate: z.string(),
    description: z.string().nullish(),
    totalAmount: z.number(),
    erpEntityId: z.string(),
    accountingPeriodId: z.string().nullish(),
    erpAmortizationTemplateId: z.string().nullish(),
    amortizationStartDate: z.string().nullish(),
    amortizationEndDate: z.string().nullish(),
    billLines: z.array(
      z.object({
        id: z.string().nullish(),
        amount: z.number(),
        memo: z.string().nullish(),
        erpAccountId: z.string(),
        erpDepartmentMappingId: z.string().nullish(),
        erpBusinessUnitId: z.string().nullish(),
        erpAmortizationTemplateId: z.string().nullish(),
        amortizationStartDate: z.string().nullish(),
        amortizationEndDate: z.string().nullish(),
      }),
    ),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostPaymentsBillRuleSetLevelsRoute = {
  method: "POST",
  path: "/api/payments/bill-rule-sets/:billRuleSetId/levels",
  query: z.void(),
  params: z.object({
    billRuleSetId: z.string(),
  }),
  body: z.array(
    z.object({
      rules: z.array(
        z.object({
          chainOperator: z.enum(["AND", "OR"]),
          approverType: z.enum([
            "NO_APPROVERS",
            "DEPARTMENT_APPROVERS",
            "BUSINESS_UNIT_APPROVERS",
            "CUSTOM_APPROVERS",
            "MANAGER_APPROVER",
            "REQUESTOR",
          ]),
          approverGroupIds: z.string().array(),
          watcherGroupIds: z.string().array(),
          numericConditions: z.array(
            z.object({
              min: z.number().nullable(),
              max: z.number().nullable(),
              isForLineItems: z.boolean(),
            }),
          ),
          anyOfConditions: z.array(
            z.object({
              values: z.array(
                z.object({
                  id: z.string(),
                  name: z.string(),
                }),
              ),
            }),
          ),
        }),
      ),
    }),
  ),
  output: z.void(),
} as const satisfies Route;

export const GetPaymentsBillRuleSetRoute = {
  method: "GET",
  path: "/api/payments/bill-rule-sets/:billRuleSetId",
  query: z.void(),
  params: z.object({
    billRuleSetId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    id: z.string(),
    name: z.string(),
    erpExpenseAccountMappings: z.array(
      z.object({ id: z.string(), name: z.string() }),
    ),
    erpDepartmentMappings: z.array(
      z.object({ id: z.string(), name: z.string() }),
    ),
    erpClassifications: z.array(z.object({ id: z.string(), name: z.string() })),
    ruleLevels: z.array(
      z.object({
        id: z.string(),
        number: z.number(),
        // deprecated: use rules instead
        noConditionRules: z.array(
          z.object({
            id: z.string(),
            approverType: z.enum([
              "NO_APPROVERS",
              "DEPARTMENT_APPROVERS",
              "BUSINESS_UNIT_APPROVERS",
              "CUSTOM_APPROVERS",
              "MANAGER_APPROVER",
              "REQUESTOR",
            ]),
            approvers: z.array(
              z.object({
                id: z.string(),
                name: z.string(),
              }),
            ),
            watchers: z.array(
              z.object({
                id: z.string(),
                name: z.string(),
              }),
            ),
          }),
        ),
        // deprecated: use rules instead
        spendCostRules: z.array(
          z.object({
            id: z.string(),
            min: z.number().nullable(),
            max: z.number().nullable(),
            isForLineItems: z.boolean(),
            approverType: z.enum([
              "NO_APPROVERS",
              "DEPARTMENT_APPROVERS",
              "BUSINESS_UNIT_APPROVERS",
              "CUSTOM_APPROVERS",
              "MANAGER_APPROVER",
              "REQUESTOR",
            ]),
            approvers: z.array(
              z.object({
                id: z.string(),
                name: z.string(),
              }),
            ),
            watchers: z.array(
              z.object({
                id: z.string(),
                name: z.string(),
              }),
            ),
          }),
        ),
        rules: z.array(
          z.object({
            id: z.string(),
            chainOperator: z.enum(["AND", "OR"]),
            approverType: z.enum([
              "NO_APPROVERS",
              "DEPARTMENT_APPROVERS",
              "BUSINESS_UNIT_APPROVERS",
              "CUSTOM_APPROVERS",
              "MANAGER_APPROVER",
              "REQUESTOR",
            ]),
            approvers: z.array(
              z.object({
                id: z.string(),
                name: z.string(),
              }),
            ),
            watchers: z.array(
              z.object({
                id: z.string(),
                name: z.string(),
              }),
            ),
            ruleConditionNumerics: z.array(
              z.object({
                min: z.number().nullable(),
                max: z.number().nullable(),
                isForLineItems: z.boolean(),
              }),
            ),
            ruleConditionAnyOfs: z.array(
              z.object({
                values: z.array(
                  z.object({
                    id: z.string(),
                    name: z.string(),
                  }),
                ),
              }),
            ),
          }),
        ),
      }),
    ),
  }),
} as const satisfies Route;

export const PatchPaymentsBillRuleSetRoute = {
  method: "PATCH",
  path: "/api/payments/bill-rule-sets/:billRuleSetId",
  query: z.void(),
  params: z.object({
    billRuleSetId: z.string(),
  }),
  body: z.object({
    name: z.string(),
  }),
  output: z.void(),
} as const satisfies Route;

export const PatchSpendRuleSetRoute = {
  method: "PATCH",
  path: "/api/spend-rule-sets/:spendRuleSetId",
  query: z.void(),
  params: z.object({
    spendRuleSetId: z.string(),
  }),
  body: z.object({
    name: z.string(),
  }),
  output: z.void(),
} as const satisfies Route;

export const GetPaymentsErpVendorPaymentMethodsRoute = {
  method: "GET",
  path: "/api/payments/erp-vendor/payment-methods",
  query: z.object({
    erpVendorIds: z.string().array().nullish(),
  }),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      createdById: z.string().nullable(),
      createdBy: z
        .object({
          id: z.string(),
          firstName: z.string().nullable(),
          lastName: z.string().nullable(),
          email: z.string(),
        })
        .nullable(),
      createdAt: z.string(),
      updatedAt: z.string(),
      isConfirmed: z.boolean(),
      isSharedByVendorCompany: z.boolean(),
      accountNumber: z.string(),
      routingNumber: z.string(),
      address1: z.string(),
      address2: z.string(),
      city: z.string(),
      state: z.string(),
      postalCode: z.string(),
      country: z.string(),
      accountHolderType: z.enum(["COMPANY", "INDIVIDUAL"]),
      accountHolderName: z.string(),
      bankAccountType: z.enum(["CHECKING", "SAVINGS"]),
      remittanceEmail: z.string().nullable(),
    }),
  ),
} as const satisfies Route;

export const GetPaymentsPaymentInformationApproversMetadataRoute = {
  method: "GET",
  path: "/api/payments/payment-information-approvers-metadata",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.object({
    paymentInformationApproversExist: z.boolean(),
    currentUserIsPaymentInformationApprover: z.boolean(),
  }),
} as const satisfies Route;

export const GetPaymentsPaymentInformationApproversRoute = {
  method: "GET",
  path: "/api/payments/payment-information-approvers",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
} as const satisfies Route;

export const PostPaymentsPaymentInformationApproversRoute = {
  method: "POST",
  path: "/api/payments/payment-information-approvers",
  query: z.void(),
  params: z.void(),
  body: z.object({
    groupIds: z.array(z.string()),
  }),
  output: z.void(),
} as const satisfies Route;

export const PutErpVendorPaymentInformationRoute = {
  method: "PUT",
  path: "/api/payments/erp-vendor-payment-information/:erpVendorPaymentInformationId",
  query: z.void(),
  params: z.object({
    erpVendorPaymentInformationId: z.string(),
  }),
  body: z.object({
    accountHolderName: z.string(),
    accountHolderType: z.enum(["COMPANY", "INDIVIDUAL"]),
    accountNumber: z.string(),
    routingNumber: z.string(),
    address1: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
    country: z.string().optional(),
    remittanceEmail: z.string().nullable().optional(),
    bankAccountType: z.enum(["CHECKING", "SAVINGS"]).optional(),
  }),
  output: z.void(),
} as const satisfies Route;

export const GetPaymentsErpEntitiesRoute = {
  method: "GET",
  path: "/api/payments/erp-entities",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.array(ErpEntitySchema),
} as const satisfies Route;

export const GetPaymentsErpEntityRoute = {
  method: "GET",
  path: "/api/payments/erp-entities/:erpEntityId",
  query: z.void(),
  params: z.object({
    erpEntityId: z.string(),
  }),
  body: z.void(),
  output: ErpEntitySchema,
} as const satisfies Route;

export const PostPaymentsErpEntitySyncAccountsRoute = {
  method: "POST",
  path: "/api/payments/erp-entity-sync-accounts",
  query: z.void(),
  params: z.void(),
  body: z.object({
    erpEntityId: z.string(),
    glFundingAccountId: z.string().nullish(),
    glClearingAccountNumber: z.string().nullish(),
    glBillPayableAccountId: z.string().nullish(),
    shouldSyncBillAutomatically: z.boolean().nullish(),
    shouldSyncBillPaymentAutomatically: z.boolean().nullish(),
  }),
  output: z.void(),
} as const satisfies Route;

export const GetPaymentsPaymentMethodsRoute = {
  method: "GET",
  path: "/api/payments/payment-methods",
  query: z.object({
    erpEntityId: z.string().nullish(),
  }),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      created: z.number(),
      type: z.string(),
      last4: z.string(),
      accountHolderName: z.string(),
      bankName: z.string(),
      isDefault: z.boolean(),
      isTest: z.boolean(),
    }),
  ),
} as const satisfies Route;

export const GetPaymentsPaymentMethodsNeedsAttentionRoute = {
  method: "GET",
  path: "/api/payments/payment-methods/needs-attention",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      createdAt: z.string(),
      updatedAt: z.string(),
      erpVendorName: z.string(),
      isSharedByVendorCompany: z.boolean(),
      createdBy: z
        .object({
          id: z.string(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string(),
        })
        .nullable(),
      accountHolderName: z.string(),
      accountHolderType: z.string(),
      address1: z.string(),
      address2: z.string(),
      city: z.string(),
      state: z.string(),
      postalCode: z.string(),
      country: z.string(),
      routingNumber: z.string(),
      accountNumber: z.string(),
    }),
  ),
} as const satisfies Route;

export const GetFinancialAccountBalanceRoute = {
  method: "GET",
  path: "/api/payments/financial-account-balance",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.object({
    available: z.number(),
    inboundPending: z.number(),
    outboundPending: z.number(),
  }),
} as const satisfies Route;

export const PostDefaultPaymentsPaymentMethodRoute = {
  method: "POST",
  path: "/api/payments/payment-method/:erpEntityId/:paymentMethodId/set-default",
  query: z.void(),
  params: z.object({
    erpEntityId: z.string(),
    paymentMethodId: z.string(),
  }),
  body: z.object({
    setAsDefault: z.boolean(),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostStartBillPaymentProcessRoute = {
  method: "POST",
  path: "/api/payments/start-bill-payment-process/:billPaymentId/:paymentMethodId",
  query: z.void(),
  params: z.object({
    billPaymentId: z.string(),
    paymentMethodId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    id: z.string(),
    amount: z.number(),
    created: z.number(),
    description: z.string().nullable(),
  }),
} as const satisfies Route;

export const PostSendBillPaymentRemittanceEmailRoute = {
  method: "POST",
  path: "/api/payments/bill-payments/:billPaymentId/send-remittance-email",
  query: z.void(),
  params: z.object({
    billPaymentId: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies Route;

export const PostTransferFundsToBankAccountRoute = {
  method: "POST",
  path: "/api/payments/transfer-funds-to-bank-account",
  query: z.void(),
  params: z.void(),
  body: z.object({
    paymentMethodId: z.string(),
    amount: z.number(),
  }),
  output: z.object({
    id: z.string(),
  }),
} as const satisfies Route;

export const DeletePaymentsPaymentMethodRoute = {
  method: "DELETE",
  path: "/api/payments/payment-method/:paymentMethodId",
  query: z.void(),
  params: z.object({
    paymentMethodId: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies Route;

export const PostPaymentsPaymentMethodSetUpIntentRoute = {
  method: "POST",
  path: "/api/payments/payment-method-setup-intent/:erpEntityId",
  query: z.void(),
  body: z.object({
    accountHolderName: z.string(),
    routingNumber: z.string(),
    accountNumber: z.string(),
  }),
  params: z.object({
    erpEntityId: z.string(),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostPaymentsPaymentMethodToErpEntityRoute = {
  method: "POST",
  path: "/api/payments/payment-method-to-erp-entity/:erpEntityId/:stripePaymentMethodId",
  query: z.void(),
  params: z.object({
    erpEntityId: z.string(),
    stripePaymentMethodId: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies Route;

export const PostPaymentsBillRoute = {
  method: "POST",
  path: "/api/payments/bill",
  query: z.void(),
  params: z.void(),
  body: CreateBillSchema,
  output: z.object({
    id: z.string(),
  }),
} as const satisfies Route;

export const GetPaymentsSetupIntentsRoute = {
  method: "GET",
  path: "/api/payments/setup-intents",
  params: z.void(),
  query: z.object({
    erpEntityId: z.string().nullish(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      status: z.string(),
      created: z.number(),
      verificationUrl: z.string().nullable(),
      expectedMicroDepositDate: z.number().nullable(),
      last4: z.string(),
    }),
  ),
} as const satisfies Route;

export const GetInvoicesRoute = {
  method: "GET",
  path: "/api/payments/invoices",
  query: paginatedQuery(z.enum(["createdAt", "id"]), z.object({})),
  params: z.void(),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      id: z.string(),
      createdAt: z.string(),
      document: z.object({
        id: z.string(),
        name: z.string(),
        displayName: z.string(),
        uploadedByEmail: z.string().nullable(),
        uploadedByUser: z
          .object({
            id: z.string(),
            firstName: z.string(),
            lastName: z.string(),
            email: z.string(),
          })
          .nullable(),
      }),
      erpEntity: z
        .object({
          id: z.string().nullish(),
          name: z.string().nullish(),
        })
        .nullish(),
      bill: z
        .object({
          id: z.string().nullish(),
          totalAmount: z.number().nullish(),
        })
        .nullish(),
    }),
  ),
} as const satisfies Route;

export const GetInvoiceRoute = {
  method: "GET",
  path: "/api/payments/invoice/:id",
  query: z.void(),
  params: z.object({ id: z.string() }),
  body: z.void(),
  output: z.object({
    id: z.string(),
    createdAt: z.string().datetime(),
    document: z.object({
      id: z.string(),
      name: z.string(),
      displayName: z.string(),
      vendor: z
        .object({
          id: z.string().nullish(),
          erpVendors: z.array(z.object({ id: z.string(), name: z.string() })),
        })
        .nullable(),
    }),
  }),
} as const satisfies Route;

export const GetInvoiceByDocIdRoute = {
  method: "GET",
  path: "/api/payments/invoice/by-document/:id",
  params: z.object({
    id: z.string(),
  }),
  query: z.void(),
  body: z.void(),
  output: z
    .object({
      id: z.string(),
      createdAt: z.string().datetime(),
      document: z.object({
        id: z.string(),
        name: z.string(),
        displayName: z.string(),
        vendor: z
          .object({
            id: z.string().nullish(),
            erpVendors: z.array(z.object({ id: z.string(), name: z.string() })),
          })
          .nullable(),
      }),
    })
    .nullable(),
} as const satisfies Route;

export const DeleteInvoiceRoute = {
  method: "DELETE",
  path: "/api/payments/invoice/:id",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies Route;

export const GetErpAccountsRoute = {
  method: "GET",
  path: "/api/erp-accounts",
  query: z.object({
    erpEntityId: z.string().nullish(),
  }),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      generalLedgerAccountNumber: z.string(),
    }),
  ),
} as const satisfies Route;

export const GetNotificationSettingsRemindersRoute = {
  method: "GET",
  path: "/api/notification-settings-reminders",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      daysAfterNotification: z.number(),
      note: z.string(),
    }),
  ),
} as const satisfies Route;

export const PostNotificationSettingsRemindersRoute = {
  method: "POST",
  path: "/api/notification-settings-reminders",
  query: z.void(),
  params: z.void(),
  body: z.object({
    daysAfterNotification: z.number(),
    note: z.string(),
  }),
  output: z.void(),
} as const satisfies Route;

export const PutNotificationSettingsRemindersRoute = {
  method: "PUT",
  path: "/api/notification-settings-reminders/:id",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.object({
    daysAfterNotification: z.number(),
    note: z.string(),
  }),
  output: z.void(),
} as const satisfies Route;

export const DeleteNotificationSettingsReminderRoute = {
  method: "DELETE",
  path: "/api/notification-settings-reminders/:id",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies Route;

export const GetNotificationSettingsRoute = {
  method: "GET",
  path: "/api/notification-settings",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.object({
    enableEmailNotifications: z.boolean(),
    enableSlackNotifications: z.boolean(),
  }),
} as const satisfies Route;

export const PutNotificationSettingsRoute = {
  method: "PUT",
  path: "/api/notification-settings",
  query: z.void(),
  params: z.void(),
  body: z.object({
    enableEmailNotifications: z.boolean(),
    enableSlackNotifications: z.boolean(),
  }),
  output: z.void(),
} as const satisfies Route;

export const GetGeneralLedgerEntriesRoute = {
  method: "GET",
  path: "/api/general-ledger-entries",
  body: z.void(),
  query: paginatedQuery(
    z.enum(["createdAt", "id"]),
    z.object({
      filter: z.enum(["SYNCED", "UNSYNCED", "ERRORED"]),
    }),
  ),
  params: z.void(),
  output: paginatedOutput(LedgerEntrySchema),
} as const satisfies Route;

export const DeleteGeneralLedgerEntryBillRoute = {
  method: "DELETE",
  path: "/api/general-ledger-entries/bills/:id",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies Route;

export const DeleteGeneralLedgerEntryBillPaymentRoute = {
  method: "DELETE",
  path: "/api/general-ledger-entries/bill-payments/:id",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies Route;

export const DeleteGeneralLedgerEntryAchLoadRoute = {
  method: "DELETE",
  path: "/api/general-ledger-entries/ach-loads/:id",
  query: z.void(),
  params: z.object({
    id: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies Route;

export const PostSuperAdminEnableCompanyFeaturesRoute = {
  method: "POST",
  path: "/api/super-admin/enable-company-features",
  query: z.void(),
  params: z.void(),
  body: z.object({
    companyId: z.string(),
    vendorIntakeEnabled: z.boolean().optional(),
    spendRequestsEnabled: z.boolean().optional(),
    intakeRequestsEnabled: z.boolean().optional(),
    emailLinkLoginEnabled: z.boolean().optional(),
    googleLoginEnabled: z.boolean().optional(),
    microsoftLoginEnabled: z.boolean().optional(),
    oktaLoginEnabled: z.boolean().optional(),
    enableOktaSamlLogin: z.boolean().optional(),
    enableBusinessUnits: z.boolean().optional(),
    forceEnableBusinessUnits: z.boolean().optional(),
    enforceIntakeApprovalOrder: z.boolean().optional(),
    enableN12mServiceSpend: z.boolean().optional(),
    enableNetsuitePurchaseOrder: z.boolean().optional(),
    enableEditWithApprovalBypass: z.boolean().optional(),
    enableCurrencies: z.boolean().optional(),
    enableSourcing: z.boolean().optional(),
    enablePayments: z.boolean().optional(),
    enablePaymentAccounting: z.boolean().optional(),
    enableForwardConfirmationEmail: z.boolean().optional(),
    enableEmailBodyPdfCreation: z.boolean().optional(),
    enableEntities: z.boolean().optional(),
    enableAllocationTransactions: z.boolean().optional(),
    restrictSubmissionEditToAdmins: z.boolean().optional(),
    enableDocumentSummary: z.boolean().optional(),
  }),
  output: z.void(),
} as const satisfies Route;

export const GetSuperAdminCompaniesRoute = {
  method: "GET",
  path: "/api/super-admin/companies",
  query: z.object({
    search: z.string().nullish(),
  }),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      emailShortName: z.string().nullable(),
      erpSyncStrategy: z
        .enum(["QUICKBOOKS", "NETSUITE", "TRIPADVISOR_CUSTOM_SYNC"])
        .nullable(),
      erpAccountClassifications: z.string().array(),
      mappedErpAccountClassifications: z.string().array(),
      requestApprovalMode: z.enum(["ALL_AT_ONCE", "EACH_LEVEL_GROUP"]),
      enableAutoCompanyLogos: z.boolean(),
      paymentsEnabled: z.boolean(),
      enablePaymentAccounting: z.boolean(),
      enableIntakeRequests: z.boolean(),
      enableSpendRequests: z.boolean(),
      enableVendorIntake: z.boolean(),
      enableEmailLinkLogin: z.boolean(),
      enableGoogleLogin: z.boolean(),
      enableMicrosoftLogin: z.boolean(),
      enableOktaLogin: z.boolean(),
      enableOktaSamlLogin: z.boolean(),
      enableBusinessUnits: z.boolean(),
      forceEnableBusinessUnits: z.boolean(),
      enforceIntakeApprovalOrder: z.boolean(),
      enableN12mServiceSpend: z.boolean(),
      enableNetsuitePurchaseOrder: z.boolean(),
      enableEditWithApprovalBypass: z.boolean(),
      enableCurrencies: z.boolean(),
      enableSourcing: z.boolean(),
      enableForwardConfirmationEmail: z.boolean(),
      enableEmailBodyPdfCreation: z.boolean(),
      enableEntities: z.boolean(),
      enableAllocationTransactions: z.boolean(),
      restrictSubmissionEditToAdmins: z.boolean(),
      enableDocumentSummary: z.boolean(),
    }),
  ),
} as const satisfies Route;

export const PostSuperAdminCompanyLogoSignedUploadUrlRoute = {
  method: "POST",
  path: "/api/super-admin/company-logo-signed-upload-url",
  params: z.void(),
  query: z.void(),
  body: z.object({
    fileName: z.string(),
    companyId: z.string(),
  }),
  output: z.object({
    url: z.string(),
    fieldsJson: z.string(),
  }),
} as const satisfies Route;

export const PostSuperAdminCompanyLogoRoute = {
  method: "POST",
  path: "/api/super-admin/company-logo",
  params: z.void(),
  query: z.void(),
  body: z.object({
    companyId: z.string(),
    lightLogoFileName: z.string().nullish(),
    darkLogoFileName: z.string().nullish(),
  }),
  output: z.void(),
} as const satisfies Route;

export const GetSuperAdminCompanyLogoSignedUrlRoute = {
  method: "GET",
  path: "/api/super-admin/company-logo-signed-url",
  params: z.void(),
  query: z.object({
    companyId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    lightLogoUrl: z.string().nullable(),
    darkLogoUrl: z.string().nullable(),
  }),
} as const satisfies Route;

export const DeleteSuperAdminCompanyLogoRoute = {
  method: "DELETE",
  path: "/api/super-admin/company-logo",
  params: z.void(),
  query: z.void(),
  body: z.object({
    companyId: z.string(),
    logoType: z.enum(["light", "dark"]),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostSuperAdminCompanyRoute = {
  method: "POST",
  path: "/api/super-admin/company",
  query: z.void(),
  params: z.void(),
  body: z.object({
    name: z.string(),
    emailDomain: z.string().nullish(),
    emailShortName: z.string(),
    mainContactEmail: z.string().nullish(),
    companyDatabase: z
      .object({
        username: z.string(),
        password: z.string(),
        hostname: z.string(),
        port: z.number(),
        database: z.string(),
        s3Bucket: z.string().nullish(),
      })
      .nullish(),
  }),
  output: z.object({
    id: z.string(),
  }),
} as const satisfies Route;

export const PutSuperAdminCompanyRoute = {
  method: "PUT",
  path: "/api/super-admin/company/:companyId",
  query: z.void(),
  params: z.object({
    companyId: z.string(),
  }),
  body: z.object({
    name: z.string(),
    emailDomain: z.string().nullish(),
    emailShortName: z.string(),
    mainContactEmail: z.string().nullable(),
  }),
  output: z.void(),
} as const satisfies Route;

export const GetSuperAdminCompanyRoute = {
  method: "GET",
  path: "/api/super-admin/company/:companyId",
  query: z.void(),
  params: z.object({
    companyId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    id: z.string(),
    name: z.string(),
    emailDomain: z.string().nullable(),
    emailShortName: z.string().nullable(),
    mainContactEmail: z.string().nullable(),
  }),
} as const satisfies Route;

export const GetCurrentCompanyRoute = {
  method: "GET",
  path: "/api/current-company",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.object({
    id: z.string(),
    name: z.string(),
    emailDomain: z.string().nullable(),
    emailShortName: z.string().nullable(),
    mainContactEmail: z.string().nullable(),
    isErpLinked: z.boolean(),
    erpIntegrationType: z
      .enum(["QUICKBOOKS", "NETSUITE", "TRIPADVISOR_CUSTOM_SYNC"])
      .nullable(),
    enableAutoCompanyLogos: z.boolean(),
    paymentsEnabled: z.boolean(),
    paymentsOnboarded: z.boolean(),
    enableIntakeRequests: z.boolean(),
    enableSpendRequests: z.boolean(),
    enableVendorIntake: z.boolean(),
    enforceIntakeApprovalOrder: z.boolean(),
    enableN12mServiceSpend: z.boolean(),
    enableNetsuitePurchaseOrder: z.boolean(),
    enableEditWithApprovalBypass: z.boolean(),
    enableCurrencies: z.boolean(),
    enableSourcing: z.boolean(),
    enablePaymentAccounting: z.boolean(),
    enableBusinessUnits: z.boolean(),
    enableEntities: z.boolean(),
    restrictSubmissionEditToAdmins: z.boolean(),
    enableDocumentSummary: z.boolean(),
  }),
} as const satisfies Route;

export const GetErpVendorLatestBillRoute = {
  method: "GET",
  path: "/api/erp-vendors/:erpVendorId/latest-bill",
  query: z.void(),
  params: z.object({
    erpVendorId: z.string(),
  }),
  body: z.void(),
  output: BillSchema.nullable(),
} as const satisfies Route;

export const GetErpAmortizationTemplatesRoute = {
  method: "GET",
  path: "/api/erp-amortization-templates",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
} as const satisfies Route;

export const GetStartIroncladWorkflowTaskStatusRoute = {
  method: "GET",
  path: "/api/start-ironclad-workflow-tasks/:startIroncladWorkflowTaskId/status",
  query: z.void(),
  params: z.object({
    startIroncladWorkflowTaskId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    stage: z.string(),
    assignee: z.string(),
    title: z.string(),
  }),
} as const satisfies Route;

export const PostStartIroncladWorkflowTaskCompleteRoute = {
  method: "POST",
  path: "/api/start-ironclad-workflow-tasks/:startIroncladWorkflowTaskId/complete",
  query: z.void(),
  params: z.object({
    startIroncladWorkflowTaskId: z.string(),
  }),
  body: z.array(IntakeSubmissionIroncladWorkflowValueSchema),
  output: z.object({
    success: z.boolean(),
    errorMessage: z.string().nullable(),
  }),
} as const satisfies Route;

export const PostAdditionalStepTaskCompleteRoute = {
  method: "POST",
  path: "/api/additional-step-tasks/:additionalStepTaskId/complete",
  query: z.void(),
  params: z.object({
    additionalStepTaskId: z.string(),
  }),
  body: z.array(IntakeFormStepSchema),
  output: z.void(),
} as const satisfies Route;

export const GetTransactionMetadataRoute = {
  method: "GET",
  path: "/api/transactions/:transactionId/metadata",
  query: z.void(),
  params: z.object({
    transactionId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    transactionId: z.string(),
    metadata: z.record(z.string(), z.unknown()),
  }),
} as const satisfies QueryRoute;

export const PostNetsuitePurchaseOrderCompleteRoute = {
  method: "POST",
  path: "/api/netsuite-purchase-orders/:netsuitePurchaseOrderId/complete",
  query: z.void(),
  params: z.object({
    netsuitePurchaseOrderId: z.string(),
  }),
  body: z.object({
    transactionDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    erpVendorId: z.string(),
    erpEntityId: z.string(),
    erpDepartmentId: z.string().nullable(),
    erpClassificationId: z.string().nullable(),
    erpLocationId: z.string().nullable(),
    memo: z.string(),
    lines: z.array(
      z.object({
        erpAccountId: z.string(),
        erpDepartmentId: z.string().nullable(),
        erpClassificationId: z.string().nullable(),
        erpLocationId: z.string().nullable(),
        amount: z.number(),
        description: z.string(),
      }),
    ),
  }),
  output: z.object({
    lastUpdatedAt: z.number(),
  }),
} as const satisfies Route;

export const GetNetsuitePurchaseOrderCompleteStatusRoute = {
  method: "GET",
  path: "/api/netsuite-purchase-orders/:netsuitePurchaseOrderId/complete",
  query: z.void(),
  params: z.object({
    netsuitePurchaseOrderId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    completed: z.boolean(),
    error: z.string().nullable(),
    lastUpdatedAt: z.number().nullable(),
  }),
} as const satisfies Route;

export const GetPurchaseOrdersRoute = {
  method: "GET",
  path: "/api/purchase-orders",
  query: paginatedQuery(
    z.enum(["syncedOn", "erpReadableId", "erpVendor", "id"]),
    z.object({}),
  ),
  params: z.void(),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      id: z.string(),
      syncedOn: z.string(),
      erpId: z.string(),
      erpReadableId: z.string(),
      url: z.string().nullable(),
      erpVendor: z.string(),
      intakeSubmission: z.object({
        id: z.string(),
        number: z.number(),
        title: z.string().nullable(),
        intakeCategory: z.object({
          id: z.string(),
          name: z.string(),
          type: IntakeCategoryType,
        }),
      }),
      lines: z.array(
        z.object({
          amount: z.number(),
          erpAccount: z.object({
            id: z.string(),
            name: z.string(),
          }),
          erpDepartment: z
            .object({
              id: z.string(),
              name: z.string(),
            })
            .nullable(),
        }),
      ),
    }),
  ),
} as const satisfies Route;

export const GetCustomFormFieldsRoute = {
  method: "GET",
  path: "/api/custom-form-fields",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.object({
    vendorFormFields: z.array(IntakeQuestionSchema),
    serviceFormFields: z.array(IntakeQuestionSchema),
    serviceItemFormFields: z.array(IntakeQuestionSchema),
  }),
} as const satisfies Route;

export const PostCustomFormFieldsRoute = {
  method: "POST",
  path: "/api/custom-form-fields",
  query: z.void(),
  params: z.void(),
  body: z.object({
    vendorFormFields: z.array(CreateIntakeQuestionSchema),
    serviceFormFields: z.array(CreateIntakeQuestionSchema),
    serviceItemFormFields: z.array(CreateIntakeQuestionSchema),
  }),
  output: z.void(),
} as const satisfies Route;

export const GetPurchaseOrderRoute = {
  method: "GET",
  path: "/api/purchase-orders/:purchaseOrderId",
  query: z.void(),
  params: z.object({
    purchaseOrderId: z.string(),
  }),
  body: z.void(),
  output: PurchaseOrderSchema,
} as const satisfies Route;

export const PostPurchaseOrderThreadRoute = {
  method: "POST",
  path: "/api/purchase-orders/:purchaseOrderId/threads",
  query: z.void(),
  params: z.object({
    purchaseOrderId: z.string(),
  }),
  body: z.object({
    commentBody: z.string(),
    documentIds: z.array(z.string()),
  }),
  output: z.void(),
} as const satisfies Route;

export const GetRequestAnalyticsAverageProcessingTimeRoute = {
  method: "GET",
  path: "/api/reports/request-analytics/average-processing-time",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      yearMonth: z.string().regex(/^\d{4}-\d{2}$/),
      averageProcessingTimeHours: z.number(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetRequestAnalyticsAverageProcessingTimeByMonthPerRequestCategoryRoute =
  {
    method: "GET",
    path: "/api/reports/request-analytics/average-processing-time-by-month-per-request-category",
    query: z.object({
      startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
      endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    }),
    body: z.void(),
    params: z.void(),
    output: z.array(
      z.object({
        yearMonth: z.string().regex(/^\d{4}-\d{2}$/),
        intakeCategoryId: z.string(),
        intakeCategoryName: z.string(),
        averageProcessingTimeHours: z.number(),
      }),
    ),
    stream: true,
  } as const satisfies Route;

export const GetRequestApprovalsByAssigneeRoute = {
  method: "GET",
  path: "/api/reports/request-analytics/by-assignee",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      assigneeUserId: z.string(),
      assigneeFirstName: z.string(),
      assigneeLastName: z.string(),
      assigneeEmail: z.string(),
      pending: z.number(),
      approved: z.number(),
      rejected: z.number(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetRequestCountByMonthRoute = {
  method: "GET",
  path: "/api/reports/request-analytics/request-count-by-month",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      yearMonth: z.string().regex(/^\d{4}-\d{2}$/),
      count: z.number(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetRequestAmountByMonthRoute = {
  method: "GET",
  path: "/api/reports/request-analytics/request-amount-by-month",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      yearMonth: z.string().regex(/^\d{4}-\d{2}$/),
      amount: z.number(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetRequestAnalyticsAverageProcessingTimeByApprovalGroupRoute = {
  method: "GET",
  path: "/api/reports/request-analytics/average-processing-time-by-approval-group",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      id: z.string().nullable(),
      name: z.string(),
      pendingTimeHours: z.number(),
      waitingOnVendorTimeHours: z.number(),
      waitingOnRequesterTimeHours: z.number(),
      waitingOnExternalPartyTimeHours: z.number(),
      count: z.number(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetSpendReviewTotalSpendRoute = {
  method: "GET",
  path: "/api/reports/spend-review/total-spend",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(z.object({ year: z.string(), amount: z.number() })),
  stream: true,
} as const satisfies Route;

export const GetSpendReviewSpendIncreasesByCategoryRoute = {
  method: "GET",
  path: "/api/reports/spend-review/spend-increases-by-category",
  query: z.object({
    startMonth: z.string().regex(/\d{2}$/),
    endMonth: z.string().regex(/\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      spendCategory: z.string(),
      currentYearSpend: z.number(),
      priorYearSpend: z.number(),
      tertiaryYearSpend: z.number(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetSpendReviewSpendIncreasesByVendorRoute = {
  method: "GET",
  path: "/api/reports/spend-review/spend-increases-by-vendor",
  query: z.object({
    startMonth: z.string().regex(/\d{2}$/),
    endMonth: z.string().regex(/\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      vendorName: z.string(),
      currentYearSpend: z.number(),
      priorYearSpend: z.number(),
      tertiaryYearSpend: z.number(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetSpendReviewYearOverYearVarianceRoute = {
  method: "GET",
  path: "/api/reports/spend-review/year-over-year-variance",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      year: z.string(),
      amountDelta: z.number().nullable(),
      amountPercent: z.number().nullable(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetSpendReviewInvoiceCountRoute = {
  method: "GET",
  path: "/api/reports/spend-review/invoice-count",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      year: z.string(),
      invoiceCount: z.string(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetSpendReviewVendorCountRoute = {
  method: "GET",
  path: "/api/reports/spend-review/vendor-count",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      year: z.string(),
      vendorCount: z.string(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetSpendReviewSpendByBusinessUnitRoute = {
  method: "GET",
  path: "/api/reports/spend-review/spend-by-business-unit",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      businessUnit: z.string(),
      totalSpend: z.number(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetSpendReviewSpendByCategoryRoute = {
  method: "GET",
  path: "/api/reports/spend-review/spend-by-category",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      spendCategory: z.string(),
      invoiceSpend: z.number(),
      servicesAmount: z.number().nullable(),
      requestAmount: z.number().nullable(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetSpendReviewSpendByDepartmentRoute = {
  method: "GET",
  path: "/api/reports/spend-review/spend-by-department",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      department: z.string(),
      invoiceSpend: z.number(),
      servicesAmount: z.number().nullable(),
      requestAmount: z.number().nullable(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetSpendReviewSpendByVendorRoute = {
  method: "GET",
  path: "/api/reports/spend-review/spend-by-vendor",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      vendorName: z.string(),
      invoiceSpend: z.number(),
      servicesAmount: z.number().nullable(),
      requestAmount: z.number().nullable(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetSpendReviewVendorCountByBusinessUnitRoute = {
  method: "GET",
  path: "/api/reports/spend-review/vendor-count-by-business-unit",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      businessUnit: z.string(),
      vendorCount: z.number(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetRequestAnalyticsByStatusRoute = {
  method: "GET",
  path: "/api/reports/request-analytics/by-status",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z
    .object({
      approved: z.number(),
      pending: z.number(),
      rejected: z.number(),
    })
    .array(),
  stream: true,
} as const satisfies Route;

export const GetRequestAnalyticsAverageProcessingTimeByRequestDepartmentRoute =
  {
    method: "GET",
    path: "/api/reports/request-analytics/average-processing-time-by-department",
    query: z.object({
      startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
      endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    }),
    body: z.void(),
    params: z.void(),
    output: z.array(
      z.object({
        id: z.string().nullable(),
        name: z.string(),
        averageProcessingTimeHours: z.number(),
        count: z.number(),
      }),
    ),
    stream: true,
  } as const satisfies Route;

export const GetRequestAnalyticsAverageProcessingTimeByMonthPerGroupRoute = {
  method: "GET",
  path: "/api/reports/request-analytics/average-processing-time-by-month-per-group",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      yearMonth: z.string().regex(/^\d{4}-\d{2}$/),
      groupId: z.string().nullable(),
      groupName: z.string(),
      averageProcessingTimeHours: z.number(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetRequestAnalyticsTotalRequestsByCategoryRoute = {
  method: "GET",
  path: "/api/reports/request-analytics/total-requests-by-category",
  query: z.object({
    startYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
    endYearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      intakeCategoryId: z.string(),
      intakeCategoryName: z.string(),
      approved: z.number(),
      pending: z.number(),
      rejected: z.number(),
    }),
  ),
  stream: true,
} as const satisfies Route;

export const GetUsersForMentionRoute = {
  method: "GET",
  path: "/api/users-for-mention",
  query: z.object({
    query: z.string(),
    vendorCompanyId: z.string().nullish(),
  }),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      display: z.string(),
    }),
  ),
} as const satisfies Route;

export const GetIntakeSubmissionUsersForMentionRoute = {
  method: "GET",
  path: "/api/intake-submissions/:intakeSubmissionId/users-for-mention",
  query: z.object({
    query: z.string(),
    includeVendorCompany: z.boolean().nullish(),
  }),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      display: z.string(),
    }),
  ),
} as const satisfies Route;

export const GetIntakeSubmissionsFilterOptionsRoute = {
  method: "GET",
  path: "/api/intake-submissions/filter-options",
  body: z.void(),
  params: z.void(),
  query: paginatedQuery(
    z.enum(["label"]),
    z.object({
      intakeCategoryId: z.string().nullish(),
      search: z.string().nullish(),
      intakeCategoryType: z
        .enum(["INTERNAL", "PUBLIC", "VENDOR", "CUSTOM_VENDOR_FORM", "SERVICE"])
        .nullish()
        .default("INTERNAL"),
    }),
  ),
  output: paginatedOutput(
    z.object({
      label: z.string(),
      value: z.string(),
      type: z.enum([
        "BUSINESS_UNIT_SELECT",
        "CUSTOM_SELECT",
        "DEPARTMENT_SELECT",
        "MEMBER_SELECT",
        "NEEDS_REQUEST_SIGNER",
        "REQUEST_OWNER_SELECT",
        "SPEND_CATEGORY_SELECT",
        "VENDOR_SELECT",
        "YES_NO",
      ]),
    }),
  ),
} as const satisfies Route;

export const GetIntakeQuestionCustomSelectOptionsRoute = {
  method: "GET",
  path: "/api/intake-questions/custom-select-options",
  query: z.object({
    questionLabel: z.string(),
  }),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
    }),
  ),
} as const satisfies Route;

export const GetIntakeSubmissionFilterQueriesRoute = {
  method: "GET",
  path: "/api/intake-submissions/filter-queries",
  body: z.void(),
  params: z.void(),
  query: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      queryString: z.string(),
      createdAt: z.string(),
      updatedAt: z.string(),
    }),
  ),
} as const satisfies Route;

export const PostIntakeSubmissionFilterQueryRoute = {
  method: "POST",
  path: "/api/intake-submissions/filter-queries",
  body: z.object({
    id: z.string().nullish(),
    label: z.string(),
    queryString: z.string(),
  }),
  params: z.void(),
  query: z.void(),
  output: z.object({
    id: z.string(),
  }),
} as const satisfies Route;

export const DeleteIntakeSubmissionFilterQueryRoute = {
  method: "DELETE",
  path: "/api/intake-submissions/filter-queries/:id",
  body: z.void(),
  params: z.object({
    id: z.string(),
  }),
  query: z.void(),
  output: z.void(),
} as const satisfies Route;

export const GetInvoicePredictionsRoute = {
  method: "GET",
  path: "/api/invoice-predictions/:invoiceId",
  body: z.void(),
  output: z.object({
    entities: z
      .array(
        z.object({
          type: z.string(),
          mentionText: z.string(),
          confidence: z.number(),
          pageAnchor: z
            .object({
              pageRefs: z.array(
                z.object({
                  page: z.string(), // 0 indexed
                  boundingPoly: z
                    .object({
                      normalizedVertices: z.array(
                        z.object({
                          x: z.number(),
                          y: z.number(),
                        }),
                      ),
                    })
                    .nullish(),
                }),
              ),
            })
            .nullable(),
          properties: z.array(
            z.object({
              type: z.string(),
              mentionText: z.string(),
              pageAnchor: z
                .object({
                  pageRefs: z.array(
                    z.object({
                      page: z.string(), // 0 indexed
                      boundingPoly: z
                        .object({
                          normalizedVertices: z.array(
                            z.object({
                              x: z.number(),
                              y: z.number(),
                            }),
                          ),
                        })
                        .nullish(),
                    }),
                  ),
                })
                .nullable(),
            }),
          ),
        }),
      )
      .nullish(),
    pages: z.array(
      z.object({
        image: z.object({
          content: z.object({
            data: z.array(z.number()),
            type: z.string(),
          }),
          mimeType: z.string(),
        }),
      }),
    ),
  }),
  stream: true,
  params: z.object({
    invoiceId: z.string(),
  }),
  query: z.void(),
} as const satisfies Route;

export const PutCommentRoute = {
  method: "PUT",
  path: "/api/comments/:commentId",
  body: z.object({
    body: z.string(),
  }),
  output: z.void(),
  params: z.object({
    commentId: z.string(),
  }),
  query: z.void(),
} as const satisfies Route;

export const DeleteCommentRoute = {
  method: "DELETE",
  path: "/api/comments/:commentId",
  body: z.void(),
  output: z.void(),
  params: z.object({
    commentId: z.string(),
  }),
  query: z.void(),
} as const satisfies Route;

export const PostMiddeskApiKeyRoute = {
  method: "POST",
  path: "/api/middesk/api-key",
  body: z.object({
    apiKey: z.string(),
  }),
  output: z.void(),
  params: z.void(),
  query: z.void(),
} as const satisfies Route;

export const GetMiddeskConfigurationRoute = {
  method: "GET",
  path: "/api/middesk/configuration",
  body: z.void(),
  query: z.void(),
  output: z.object({
    connected: z.boolean(),
  }),
  params: z.void(),
} as const satisfies Route;

export const GetVendorVerificationsRoute = {
  method: "GET",
  path: "/api/vendors/:vendorId/verifications",
  query: z.void(),
  params: z.object({
    vendorId: z.string(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      createdAt: z.string(),
      syncedAt: z.string().nullable(),
      businessName: z.string(),
      businessAddress1: z.string(),
      businessAddress2: z.string().nullable(),
      businessCity: z.string(),
      businessState: z.string(),
      businessPostalCode: z.string(),
      businessTaxId: z.string().nullable(),
      document: z
        .object({
          id: z.string(),
          name: z.string(),
          displayName: z.string(),
        })
        .nullable(),
      verifiedAt: z.string().nullable(),
    }),
  ),
} as const satisfies Route;

export const PostVendorVerificationRoute = {
  method: "POST",
  path: "/api/vendors/:vendorId/verifications",
  query: z.void(),
  params: z.object({
    vendorId: z.string(),
  }),
  body: z.object({
    businessName: z.string(),
    businessAddress1: z.string(),
    businessAddress2: z.string().nullable(),
    businessCity: z.string(),
    businessState: z.string(),
    businessPostalCode: z.string(),
    businessCountry: z.string(),
    businessTaxId: z.string().nullable(),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostMiddeskBusinessVerificationTaskCompleteRoute = {
  method: "POST",
  path: "/api/middesk-business-verification-tasks/:middeskVerificationTaskId/complete",
  query: z.void(),
  params: z.object({
    middeskVerificationTaskId: z.string(),
  }),
  body: z.object({
    businessName: z.string(),
    businessAddress1: z.string(),
    businessAddress2: z.string().nullable(),
    businessCity: z.string(),
    businessState: z.string(),
    businessPostalCode: z.string(),
    businessTaxId: z.string().nullable(),
    businessCountry: z.string(),
  }),
  output: z.void(),
} as const satisfies Route;

export const GetServicePeriodRenewalReminderConfigRoute = {
  method: "GET",
  path: "/api/service-period-renewal-reminder-config",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.object({
    message: z.string(),
    sendToAdmins: z.boolean(),
    sendToVendorOwners: z.boolean(),
    sendToServiceOwners: z.boolean(),
    sendToDepartmentLeads: z.boolean(),
    sendToBusinessUnitLeads: z.boolean(),
    groupsToSendTo: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    ),
  }),
} as const satisfies Route;

export const PostServicePeriodRenewalReminderConfigRoute = {
  method: "POST",
  path: "/api/service-period-renewal-reminder-config",
  query: z.void(),
  params: z.void(),
  body: z.object({
    message: z.string(),
    sendToAdmins: z.boolean(),
    sendToVendorOwners: z.boolean(),
    sendToServiceOwners: z.boolean(),
    sendToDepartmentLeads: z.boolean(),
    sendToBusinessUnitLeads: z.boolean(),
    groupsToSendTo: z.array(z.string()),
  }),
  output: z.void(),
} as const satisfies Route;

export const GetErpEntitiesRoute = {
  method: "GET",
  path: "/api/erp-entities",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      erpId: z.string(),
      name: z.string(),
      entityId: z.string().nullable(),
    }),
  ),
} as const satisfies Route;

export const PostErpEntityMappingsRoute = {
  method: "POST",
  path: "/api/erp-entities/mappings",
  query: z.void(),
  params: z.void(),
  body: z.array(
    z.object({
      id: z.string(),
      entityId: z.string().nullable(),
      newEntityName: z.string().nullable(),
    }),
  ),
  output: z.void(),
} as const satisfies Route;

export const GetEntitiesRoute = {
  method: "GET",
  path: "/api/entities",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
} as const satisfies Route;

export const PostEntitiesRoute = {
  method: "POST",
  path: "/api/entities",
  query: z.void(),
  params: z.void(),
  body: z.object({
    entities: z.array(
      z.object({
        id: z.string().optional(),
        name: z.string(),
      }),
    ),
  }),
  output: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
} as const satisfies Route;

export const GetUnmappedErpObjectCountsRoute = {
  method: "GET",
  path: "/api/unmapped-erp-object-counts",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.object({
    expenseAccounts: z.number(),
    revenueAccounts: z.number(),
    locations: z.number(),
    departments: z.number(),
    vendors: z.number(),
    classifications: z.number(),
    entities: z.number(),
  }),
} as const satisfies Route;

export const GetPlanDepartmentsRoute = {
  method: "GET",
  path: "/api/plans/:planId/plan-departments",
  query: z.void(),
  params: z.object({ planId: z.string() }),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      planMappings: z.array(
        z.object({
          department: z.object({ id: z.string(), name: z.string() }),
          entity: z.object({ id: z.string(), name: z.string() }).nullable(),
        }),
      ),
    }),
  ),
} as const satisfies Route;

export const PutPlanDepartmentRoute = {
  method: "PUT",
  path: "/api/plans/:planId/plan-departments/:planDepartmentId/mappings",
  query: z.void(),
  params: z.object({
    planId: z.string(),
    planDepartmentId: z.string(),
  }),
  body: z.object({
    name: z.string(),
    mappings: z.array(
      z.object({
        departmentId: z.string(),
        entityId: z.string().nullable(),
      }),
    ),
  }),
  output: z.void(),
} as const satisfies Route;

export const DeleteApproverMatrixRoute = {
  method: "DELETE",
  path: "/api/approver-matrix/:approverMatrixId",
  query: z.void(),
  params: z.object({
    approverMatrixId: z.string(),
  }),
  body: z.void(),
  output: z.void(),
} as const satisfies Route;

export const PostApproverMatrixRoute = {
  method: "POST",
  path: "/api/approver-matrix",
  query: z.void(),
  params: z.void(),
  body: z.object({
    name: z.string(),
  }),
  output: z.object({
    id: z.string(),
    name: z.string(),
  }),
} as const satisfies Route;

export const PutApproverMatrixRoute = {
  method: "PUT",
  path: "/api/approver-matrix/:approverMatrixId",
  query: z.void(),
  params: z.object({
    approverMatrixId: z.string(),
  }),
  body: z.object({
    name: z.string(),
  }),
  output: z.object({
    id: z.string(),
    name: z.string(),
  }),
} as const satisfies Route;

const ApproverMatrixConditionType = z.enum([
  "DEPARTMENT",
  "ENTITY",
  "BUSINESS_UNIT",
  "CATEGORY",
]);

export type ApproverMatrixConditionType = z.infer<
  typeof ApproverMatrixConditionType
>;

export const GetApproverMatricesRoute = {
  method: "GET",
  path: "/api/approver-matrix",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      approverMatrixConditionColumns: z.array(
        z.object({
          id: z.string(),
          conditionType: ApproverMatrixConditionType,
        }),
      ),
      approverMatrixApproverColumns: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
        }),
      ),
    }),
  ),
} as const satisfies Route;

export const GetApproverMatrixRoute = {
  method: "GET",
  path: "/api/approver-matrix/:approverMatrixId",
  query: z.void(),
  params: z.object({
    approverMatrixId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    id: z.string(),
    name: z.string(),
    approverMatrixConditionColumns: z.array(
      z.object({
        id: z.string(),
        conditionType: ApproverMatrixConditionType,
        ordinal: z.number(),
        approverMatrixConditionCells: z.array(
          z.object({
            id: z.string(),
            ordinal: z.number(),
            values: z.array(
              z.object({
                id: z.string(),
                name: z.string(),
              }),
            ),
          }),
        ),
      }),
    ),
    approverMatrixApproverColumns: z.array(
      z.object({
        id: z.string(),
        ordinal: z.number(),
        name: z.string(),
        approverMatrixApproverCells: z.array(
          z.object({
            id: z.string(),
            ordinal: z.number(),
            users: z.array(
              z.object({
                id: z.string(),
                name: z.string(),
              }),
            ),
          }),
        ),
      }),
    ),
  }),
} as const satisfies Route;

export const PostApproverMatrixLogicRoute = {
  method: "POST",
  path: "/api/approver-matrix/:approverMatrixId/logic",
  query: z.void(),
  params: z.object({
    approverMatrixId: z.string(),
  }),
  body: z.object({
    id: z.string(),
    name: z.string(),
    approverMatrixConditionColumns: z.array(
      z.object({
        id: z.string(),
        conditionType: z.enum([
          "DEPARTMENT",
          "ENTITY",
          "BUSINESS_UNIT",
          "CATEGORY",
        ]),
        ordinal: z.number(),
        approverMatrixConditionCells: z.array(
          z.object({
            id: z.string(),
            ordinal: z.number(),
            values: z.array(
              z.object({
                id: z.string(),
                name: z.string(),
              }),
            ),
          }),
        ),
      }),
    ),
    approverMatrixApproverColumns: z.array(
      z.object({
        id: z.string(),
        ordinal: z.number(),
        name: z.string(),
        approverMatrixApproverCells: z.array(
          z.object({
            id: z.string(),
            ordinal: z.number(),
            users: z.array(
              z.object({
                id: z.string(),
                name: z.string(),
              }),
            ),
          }),
        ),
      }),
    ),
  }),
  output: z.void(),
} as const satisfies Route;

export const PostDismissVendorMappingRoute = {
  method: "POST",
  path: "/api/dismiss-vendor-mapping",
  query: z.void(),
  params: z.void(),
  body: z.array(
    z.object({
      id: z.string(),
      isDismissed: z.boolean(),
    }),
  ),
  output: z.void(),
} as const satisfies Route;

// PUBLIC ROUTES
export const GetPublicIntakeQuestionOptionsRoute = {
  method: "GET",
  path: "/public-api/intake-questions/:intakeQuestionId/options",
  query: z.object({
    search: z.string().nullish(),
  }),
  params: z.object({
    intakeQuestionId: z.string(),
  }),
  body: z.void(),
  output: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
    }),
  ),
} as const satisfies PublicQueryRoute;

// VENDOR PORTAL ROUTES
export const GetVendorPortalThreadCommentLinesRoute = {
  method: "GET",
  path: "/api/v/comments/:threadId",
  params: z.object({
    threadId: z.string(),
  }),
  query: paginatedQuery(z.enum(["createdAt", "id"]), z.object({})),
  body: z.void(),
  output: paginatedOutput(
    z.object({
      id: z.string(),
      createdAt: z.string(),
      updatedAt: z.string(),
      deletedAt: z.string().nullable(),
      body: z.string(),
      createdByUser: z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      }),
    }),
  ),
} as const satisfies Route;

export const PutVendorPortalCommentRoute = {
  method: "PUT",
  path: "/api/v/comments/:commentId",
  body: z.object({
    body: z.string(),
  }),
  output: z.void(),
  params: z.object({
    commentId: z.string(),
  }),
  query: z.void(),
} as const satisfies Route;

export const DeleteVendorPortalCommentRoute = {
  method: "DELETE",
  path: "/api/v/comments/:commentId",
  body: z.void(),
  output: z.void(),
  params: z.object({
    commentId: z.string(),
  }),
  query: z.void(),
} as const satisfies Route;

export const GetVendorPortalSessionRoute = {
  method: "GET",
  path: "/api/v/session",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.object({
    vendorCompany: z.object({
      id: z.string(),
      name: z.string(),
      vendorType: z.enum(["BUSINESS", "INDIVIDUAL"]),
    }),
  }),
} as const satisfies Route;

export const GetVendorPortalAssociatedCompaniesRoute = {
  method: "GET",
  path: "/api/v/associated-companies",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      vendorCompany: z.object({ id: z.string(), name: z.string() }),
      company: z.object({ id: z.string(), name: z.string() }),
    }),
  ),
} as const satisfies Route;

export const PostVendorPortalTokenWithCompanyRoute = {
  method: "POST",
  path: "/api/v/token-with-company",
  query: z.void(),
  params: z.void(),
  body: z.object({ companyId: z.string(), vendorCompanyId: z.string() }),
  output: z.void(),
} as const satisfies Route;

export const GetVendorPortalNeedsAttentionCommentsRoute = {
  method: "GET",
  path: "/api/v/needs-attention/comments",
  body: z.void(),
  query: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      createdAt: z.string().datetime(),
      threadId: z.string(),
      user: z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      }),
      company: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      body: z.string(),
      intakeSubmissionThread: z.object({
        id: z.string(),
        intakeSubmissionId: z.string(),
        intakeSubmissionAnswerId: z.string().nullable(),
      }),
    }),
  ),
} as const satisfies Route;

export const GetVendorPortalVendorIntakeCategoryRoute = {
  method: "GET",
  path: "/api/vendor-intake/:vendorCompanyIntakeInvitationId/:intakeCategoryId/category",
  query: z.void(),
  params: z.object({
    vendorCompanyIntakeInvitationId: z.string(),
    intakeCategoryId: z.string(),
  }),
  body: z.void(),
  output: z
    .object({
      id: z.string(),
      name: z.string(),
      description: z.string(),
      createdAt: z.string().datetime(),
      updatedAt: z.string().datetime(),
      companyId: z.string(),
    })
    .nullable(),
} as const satisfies Route;

export const GetVendorPortalVendorIntakeInvitationsRoute = {
  method: "GET",
  path: "/api/vendor-intake/invitations",
  body: z.void(),
  query: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      createdAt: z.string(),
      intakeCategory: z.object({
        id: z.string(),
        name: z.string(),
      }),
      company: z.object({
        id: z.string(),
        name: z.string(),
      }),
      intakeSubmissionId: z.string(),
    }),
  ),
} as const satisfies Route;

export const GetVendorPortalVendorIntakeFormsRoute = {
  method: "GET",
  path: "/api/vendor-intake/forms",
  body: z.void(),
  query: paginatedQuery(
    z.enum(["createdAt"]),
    z.object({
      filter: z.enum(["needs attention", "submitted", "approved"]).nullish(),
    }),
  ),
  params: z.void(),
  output: paginatedOutput(
    z.object({
      invitationId: z.string(),
      createdAt: z.string().datetime(),
      intakeCategory: z.object({
        id: z.string(),
        name: z.string(),
      }),
      company: z.object({
        id: z.string(),
        name: z.string(),
      }),
      isDraft: z.boolean(),
      status: z.enum(["UNSUBMITTED", "PENDING", "APPROVED", "REJECTED"]),
      owner: z
        .object({
          id: z.string(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string(),
        })
        .nullable(),
      intakeSubmissionId: z.string(),
    }),
  ),
} as const satisfies Route;

export const GetVendorPortalVendorIntakeFormStepsRoute = {
  method: "GET",
  path: "/api/vendor-intake/forms/:intakeSubmissionId/steps",
  body: z.void(),
  query: paginatedQuery(z.enum(["id"]), z.object({})),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  output: paginatedOutput(IntakeQuestionSchema),
} as const satisfies Route;

export const GetVendorPortalVendorIntakeFormSubmissionRoute = {
  method: "GET",
  path: "/api/vendor-intake/forms/:intakeSubmissionId",
  body: z.void(),
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  output: GetIntakeSubmissionSchema,
} as const satisfies Route;

export const GetVendorPortalVendorIntakeFormAnswersRoute = {
  method: "GET",
  path: "/api/vendor-intake/forms/:intakeSubmissionId/answers",
  body: z.void(),
  query: paginatedQuery(
    z.enum(["stepOrdinal", "fieldOrdinal", "id"]),
    z.object({}),
  ),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  output: paginatedOutput(IntakeAnswer),
} as const satisfies Route;

export const PostVendorPortalSignedUploadUrlRoute = {
  method: "POST",
  path: "/api/v/signed-upload-url",
  query: z.void(),
  params: z.void(),
  body: z.object({
    fileName: z.string(),
  }),
  output: z.object({
    url: z.string(),
    fieldsJson: z.string(),
  }),
} as const satisfies Route;

export const PostVendorPortalIntakeSubmissionThreadRoute = {
  method: "POST",
  path: "/api/v/intake-submissions/:intakeSubmissionId/threads",
  output: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  query: z.void(),
  body: z.object({
    intakeSubmissionAnswerId: z.string().nullish(),
    commentBody: z.string(),
    documentIds: z.array(z.string()),
  }),
} as const satisfies Route;

export const GetVendorPortalIntakeSubmissionThreadsRoute = {
  method: "GET",
  path: "/api/v/intake-submissions/:intakeSubmissionId/threads",
  output: paginatedOutput(GetIntakeSubmissionThreadsOutput),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  query: paginatedQuery(
    z.enum(["createdAt", "id"]),
    z.object({
      intakeSubmissionAnswerId: z.string().nullish(),
    }),
  ),
  body: z.void(),
} as const satisfies Route;

export const PostVendorPortalThreadReplyRoute = {
  method: "POST",
  path: "/api/v/threads/:threadId/reply",
  output: z.void(),
  params: z.object({
    threadId: z.string(),
  }),
  query: z.void(),
  body: z.object({
    commentBody: z.string(),
    documentIds: z.array(z.string()),
  }),
} as const satisfies Route;

export const PostVendorPortalDocumentsRoute = {
  method: "POST",
  path: "/api/v/documents",
  query: z.void(),
  params: z.void(),
  body: z.object({
    name: z.string(),
    displayName: z.string(),
    type: z.array(DocumentTypeSchema),
    mimeType: z.string(),
    url: z.string(),
  }),
  output: z.object({
    id: z.string(),
    name: z.string(),
    displayName: z.string(),
    mimeType: z.string(),
  }),
} as const satisfies Route;

export const PostVendorPortalVendorIntakeFormSubmitRoute = {
  method: "POST",
  path: "/api/vendor-intake/forms/:intakeSubmissionId/submit",
  query: z.void(),
  params: z.object({
    intakeSubmissionId: z.string(),
  }),
  body: z.object({
    steps: IntakeFormStepSchema.array(),
    isDraft: z.boolean(),
    version: z.number(),
    batch: z.number(),
    totalBatches: z.number(),
  }),
  output: z.object({
    id: z.string(),
    version: z.number(),
  }),
} as const satisfies Route;

export const GetVendorPortalUsersForMentionsRoute = {
  method: "GET",
  path: "/api/v/users-for-mentions",
  query: z.object({
    query: z.string(),
  }),
  params: z.void(),
  body: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      display: z.string(),
    }),
  ),
} as const satisfies Route;

// END VENDOR PORTAL ROUTES

export const PostPaymentsBillsApproversPreviewRoute = {
  method: "POST",
  path: "/api/payments/bills/approvers-preview",
  query: z.void(),
  params: z.void(),
  body: z.object({
    totalAmount: z.number(),
    billLines: z.array(
      z.object({
        amount: z.number().nullish(),
        erpAccountId: z.string().nullish(),
        erpBusinessUnitId: z.string().nullish(),
        erpDepartmentId: z.string().nullish(),
      }),
    ),
  }),
  output: BillApproversSchema.array(),
} as const satisfies Route;

export const PostCreateSyncJobRoute = {
  method: "POST",
  path: "/api/create-sync-job",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.void(),
} as const satisfies Route;

export const GetSyncStatusRoute = {
  method: "GET",
  path: "/api/get-sync-status",
  query: z.void(),
  params: z.void(),
  body: z.void(),
  output: z.enum(["SYNCING", "COMPLETE", "ERROR"]),
} as const satisfies Route;

// GLOBAL ROUTES (Sudozi company OR vendor portal)
export const GetGlobalDocumentsFromIdsRoute = {
  method: "GET",
  path: "/api/global/documents-from-ids",
  query: z.object({
    documentId: z.union([z.string().array(), z.string()]),
    companyId: z.string().optional(),
  }),
  body: z.void(),
  params: z.void(),
  output: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      displayName: z.string(),
      mimeType: z.string(),
    }),
  ),
} as const satisfies Route;

export type ApiOutputSchema<T extends Route> = z.infer<T["output"]>;

export type ApiRouteBodyArgs<T extends Route> = T["body"] extends
  | z.ZodObject<any>
  | z.ZodArray<any>
  ? z.infer<T["body"]>
  : never;

export type ApiPaginatedRouteOutputType<T extends Route> =
  T["output"] extends z.ZodObject<any>
    ? z.infer<T["output"]>["edges"][number]["node"]
    : never;

export type ApiPaginatedOutput<TSchema extends z.ZodSchema> = z.infer<
  ReturnType<typeof paginatedOutput<TSchema>>
>;

export const PostSummarizeDocumentRoute = {
  method: "POST",
  path: "/api/documents/:documentId/summarize",
  query: z.void(),
  params: z.object({
    documentId: z.string(),
  }),
  body: z.void(),
  output: z.object({
    summary: z.string(),
    threadId: z.string(),
  }),
} as const satisfies MutationRoute;
